import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API, useAuth, getAuthenticatedMediaUrl } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Shield,
  Plus,
  Trash2,
  Edit,
  CheckCircle,
  XCircle,
  Clock,
  Car,
  MapPin,
  Users,
  Play,
  Square,
  RefreshCw,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  X,
  AlertTriangle,
  ArrowRight,
  Save,
  School,
  Construction,
  Map as MapIcon,
  Loader2,
  Navigation,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";

// ─── helpers ───────────────────────────────────────────────────────────
// Wrap axios calls with withCredentials
const ax = {
  get: (url, cfg) => axios.get(url, { ...cfg, withCredentials: true }),
  post: (url, data, cfg) => axios.post(url, data, { ...cfg, withCredentials: true }),
  put: (url, data, cfg) => axios.put(url, data, { ...cfg, withCredentials: true }),
  delete: (url, cfg) => axios.delete(url, { ...cfg, withCredentials: true }),
};

const statusColor = {
  covered: "bg-green-600 text-white",
  enroute: "bg-amber-500 text-black",
  open: "bg-red-600 text-white",
  pending: "bg-gray-500 text-white",
  cleared: "bg-blue-600 text-white",
};

const carStatusColor = {
  standby: "bg-gray-500 text-white",
  en_route: "bg-yellow-500 text-black",
  posted: "bg-green-600 text-white",
  done: "bg-blue-600 text-white",
};

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

// Render the audit-log details column as compact human-readable text.
// Picks out the rich post fields (location, car, unit, status transitions)
// instead of dumping the raw JSON. Falls back to JSON.stringify for entries
// whose details don't match the post-audit shape.
const formatAuditDetails = (entry) => {
  const d = entry?.details;
  if (!d || typeof d !== "object") return d ? String(d) : "";

  // Post-shaped detail (location/car/unit present) — render compactly.
  const isPostShaped = (
    "location" in d || "car" in d || "unit" in d ||
    "post_number" in d || "new_status" in d || "prev_status" in d ||
    "old_car" in d || "new_car" in d
  );
  if (isPostShaped) {
    const parts = [];
    // Where: location + position + specific
    const where = [d.location, d.position, d.specific].filter(Boolean).join(" / ");
    if (where) parts.push(where);
    // Car / Unit
    const carUnit = [];
    if (d.car) carUnit.push(`Car ${d.car}`);
    if (d.unit) carUnit.push(`Unit ${d.unit}`);
    if (carUnit.length) parts.push(carUnit.join(" · "));
    // Status transition
    if (d.prev_status && d.new_status && d.prev_status !== d.new_status) {
      parts.push(`status: ${d.prev_status} → ${d.new_status}`);
    } else if (d.new_status) {
      parts.push(`status: ${d.new_status}`);
    }
    // Reassign-specific
    if ("old_car" in d || "new_car" in d) {
      parts.push(`car: ${d.old_car || "(none)"} → ${d.new_car || "(none)"}${d.new_unit ? ` (Unit ${d.new_unit})` : ""}`);
    }
    // post_updated diff
    if (d.changes && typeof d.changes === "object") {
      const changeBits = Object.entries(d.changes).slice(0, 4).map(([k, v]) => {
        const oldS = v?.old == null ? "(empty)" : String(v.old);
        const newS = v?.new == null ? "(empty)" : String(v.new);
        return `${k}: '${oldS}' → '${newS}'`;
      });
      if (changeBits.length) parts.push(changeBits.join("; "));
    }
    if (d.notes) parts.push(`notes: ${d.notes}`);
    if (d.auto) parts.push("(auto)");
    if (d.triggered_by_post) parts.push(`triggered by post #${d.triggered_by_post}`);
    return parts.join(" — ") || JSON.stringify(d);
  }

  // Non-post entries (event-level go_live / complete / settings / etc):
  // show key=value pairs, skipping empty.
  const kv = Object.entries(d)
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .slice(0, 8)
    .map(([k, v]) => {
      if (typeof v === "object") return `${k}: ${JSON.stringify(v)}`;
      return `${k}: ${v}`;
    });
  return kv.join(", ");
};

// Mini-map preview of a post's location with a "Navigate" button that
// opens turn-by-turn directions in Google Maps. Returns null when the
// post has no coordinates.
function PostMapPreview({ post }) {
  if (!post?.lat || !post?.lng) return null;
  const navUrl = `https://www.google.com/maps/dir/?api=1&destination=${post.lat},${post.lng}`;
  return (
    <div className="border-t pt-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Location</span>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          onClick={() => window.open(navUrl, "_blank", "noopener,noreferrer")}
        >
          <Navigation className="h-3.5 w-3.5 mr-1" /> Navigate
        </Button>
      </div>
      <div className="rounded overflow-hidden border" style={{ height: 180 }}>
        <MapContainer
          center={[post.lat, post.lng]}
          zoom={16}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={false}
          key={`${post.post_number}-${post.lat}-${post.lng}`}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
          <Marker
            position={[post.lat, post.lng]}
            icon={createPostIcon(post.status || "pending", post.post_number || "?")}
          />
        </MapContainer>
      </div>
    </div>
  );
}

// Resolve a post's attached route ids. Posts now support multiple
// routes via post.route_ids; older posts have a single post.route_id.
// Always returns a string[] (possibly empty).
function postRouteIds(post) {
  if (!post) return [];
  if (Array.isArray(post.route_ids) && post.route_ids.length > 0) {
    return post.route_ids.filter(Boolean).map(String);
  }
  if (post.route_id) return [String(post.route_id)];
  return [];
}

// Per-post priority badge. Returns null when unset so the badge only
// appears for posts with a priority assigned.
const PRIORITY_STYLES = {
  low: "bg-slate-500 text-white",
  medium: "bg-blue-600 text-white",
  high: "bg-amber-600 text-white",
  critical: "bg-red-600 text-white",
};
function PriorityBadge({ priority }) {
  if (!priority) return null;
  const cls = PRIORITY_STYLES[priority] || "bg-slate-500 text-white";
  return <Badge className={`text-[10px] uppercase ${cls}`}>{priority}</Badge>;
}

// Searchable single-select filter — same combobox pattern used in
// LiveCalls so the user can type to narrow long option lists.
function SearchableFilter({ value, onChange, options, placeholder = "Select...", widthClass = "w-[140px]" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = options.find(o => o.value === value);
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQuery(""); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={`${widthClass} justify-between font-normal`}>
          <span className="truncate">{selected?.label || placeholder}</span>
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" style={{ width: 'var(--radix-popover-trigger-width)' }} align="start">
        <Command shouldFilter={false}>
          <CommandInput value={query} onValueChange={setQuery} placeholder="Search..." className="h-9" />
          <CommandList className="max-h-60 overflow-y-auto">
            {(() => {
              const q = query.trim().toLowerCase();
              const filtered = options.filter(o => !q || (o.label || "").toLowerCase().includes(q));
              if (filtered.length === 0) return <CommandEmpty>No match.</CommandEmpty>;
              return (
                <CommandGroup>
                  {filtered.map((o) => (
                    <CommandItem
                      key={o.value}
                      value={o.value}
                      onSelect={() => { onChange(o.value); setOpen(false); setQuery(""); }}
                      className={`cursor-pointer ${o.value === value ? "font-bold" : ""}`}
                    >
                      {o.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })()}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ─── Map helpers ──────────────────────────────────────────────────────
const statusMarkerColor = {
  pending: "#6b7280",  // gray
  enroute: "#f59e0b",  // amber
  open: "#dc2626",     // red
  covered: "#16a34a",  // green
  cleared: "#2563eb",  // blue
};

const createPostIcon = (status, label, sublabel) => {
  const color = statusMarkerColor[status] || "#6b7280";
  const hasSub = !!sublabel;
  const h = hasSub ? 36 : 28;
  return L.divIcon({
    className: "custom-post-marker",
    html: `
      <div style="position: relative;">
        <div style="
          background-color: ${color};
          width: 28px;
          height: ${h}px;
          border-radius: ${hasSub ? '14px 14px 14px 0' : '50% 50% 50% 0'};
          transform: rotate(-45deg);
          border: 3px solid white;
          box-shadow: 0 2px 5px rgba(0,0,0,0.3);
        ">
          <div style="
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            transform: rotate(45deg);
            color: white;
            font-weight: bold;
            line-height: 1;
          ">
            <span style="font-size: 11px;">${label}</span>
            ${hasSub ? `<span style="font-size: 8px; opacity: 0.85;">${sublabel}</span>` : ''}
          </div>
        </div>
      </div>
    `,
    iconSize: [28, h],
    iconAnchor: [14, h],
    popupAnchor: [0, -h],
  });
};

// Crown Heights Brooklyn center
const CROWN_HEIGHTS_CENTER = [40.6694, -73.9422];

const createLeadTailIcon = (label, color) => {
  return L.divIcon({
    className: "lead-tail-marker",
    html: `<div style="background:${color || '#2563EB'};color:white;font-weight:bold;font-size:12px;width:24px;height:24px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 2px 5px rgba(0,0,0,0.3);">${label}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

const createRouteArrowIcon = (angle, color) => {
  return L.divIcon({
    className: "route-arrow",
    html: `<div style="width:20px;height:20px;display:flex;align-items:center;justify-content:center;">
      <svg width="14" height="14" viewBox="0 0 14 14" style="transform:rotate(${angle}deg);">
        <path d="M2 7 L11 7 M8 4 L11 7 L8 10" stroke="${color || '#2563EB'}" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
};

const createRouteEndpointIcon = (label, color) => {
  return L.divIcon({
    className: "route-endpoint",
    html: `<div style="background:${color || '#2563EB'};color:white;font-weight:bold;font-size:9px;width:22px;height:22px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 2px 5px rgba(0,0,0,0.3);">${label}</div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
};

function getArrowAngle(from, to) {
  // from/to are [lat, lng]. For heading: atan2(dlng, dlat) gives bearing from north.
  // SVG arrow points right (east), so subtract 90° to align.
  const dlat = to[0] - from[0];
  const dlng = to[1] - from[1];
  return (Math.atan2(dlng, dlat) * 180) / Math.PI - 90;
}

function getMidpoint(a, b) {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

// Click-to-place pin handler
function MapClickHandler({ onMapClick }) {
  const map = useMap();
  useEffect(() => {
    const handler = (e) => {
      onMapClick(parseFloat(e.latlng.lat.toFixed(6)), parseFloat(e.latlng.lng.toFixed(6)));
    };
    map.on("click", handler);
    return () => map.off("click", handler);
  }, [map, onMapClick]);
  return null;
}

// Component to fit map bounds to markers
function PostMapBoundsHandler({ posts }) {
  const map = useMap();
  const fittedRef = useRef(false);
  useEffect(() => {
    // Fit once when posts with coordinates first arrive, then leave the
    // map alone — the live poll runs every 5s and any subsequent fit
    // would yank the map back, blocking the user's pan/zoom.
    if (fittedRef.current) return;
    const withCoords = posts.filter((p) => p.lat && p.lng);
    if (withCoords.length > 0) {
      const bounds = L.latLngBounds(withCoords.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
      fittedRef.current = true;
    }
  }, [posts, map]);
  return null;
}

// ─── main component ────────────────────────────────────────────────────
export default function EventOps() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  // core state
  const [events, setEvents] = useState([]);
  const [activeEvent, setActiveEvent] = useState(null);
  const [activeTab, setActiveTab] = useState("posts");

  // detail data (loaded per event)
  const [posts, setPosts] = useState([]);
  const [cars, setCars] = useState([]);
  const [schools, setSchools] = useState([]);
  const [closures, setClosures] = useState([]);
  const [audit, setAudit] = useState([]);
  const [appUnits, setAppUnits] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [showAddUpdate, setShowAddUpdate] = useState(false);

  // dialog states
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [showEditPost, setShowEditPost] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [showEditCar, setShowEditCar] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [showEditSchool, setShowEditSchool] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [showEditClosure, setShowEditClosure] = useState(false);
  const [editingClosure, setEditingClosure] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [showEditRoute, setShowEditRoute] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [visibleRouteIds, setVisibleRouteIds] = useState(new Set());
  const [viewingRoute, setViewingRoute] = useState(null);
  const [showLeadTailDialog, setShowLeadTailDialog] = useState(false);
  const [leadTailTarget, setLeadTailTarget] = useState(null);
  const [showGoLiveConfirm, setShowGoLiveConfirm] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  // new event form
  const [newEventForm, setNewEventForm] = useState({ name: "", date: "", copy_from_event_id: "" });

  // filters
  const [postsSubTab, setPostsSubTab] = useState("list");
  const [mapPinMode, setMapPinMode] = useState("post");
  const [viewingPost, setViewingPost] = useState(null);
  const [postStatusFilter, setPostStatusFilter] = useState("all");
  const [postCarFilter, setPostCarFilter] = useState("all");
  const [postRouteFilter, setPostRouteFilter] = useState("all"); // "all" | route_id | "__none__"
  const [postSortMode, setPostSortMode] = useState("number"); // "number" | "custom" | "ring" | "priority" | "car"
  const [showCustomOrderDialog, setShowCustomOrderDialog] = useState(false);
  const [postSearchNumber, setPostSearchNumber] = useState(""); // matches post #
  const [postSearchUnit, setPostSearchUnit] = useState(""); // matches unit #
  const [postSearchAny, setPostSearchAny] = useState(""); // matches anywhere (location/specific/position/notes/...)
  // Export-cars dialog: holds the formatted text to copy/download.
  const [exportCarsText, setExportCarsText] = useState(null);
  // "Unit Roster" dialog — shows the units chosen for this event, plus
  // which car (if any) each is on. The roster lives on the event doc as
  // `unit_roster` (array of unit names) and is independent of cars.
  const [showUnitRoster, setShowUnitRoster] = useState(false);
  // Picker dialog for editing the roster (which units are on the event).
  const [showUnitRosterEdit, setShowUnitRosterEdit] = useState(false);
  const [unitRosterDraft, setUnitRosterDraft] = useState([]);
  const [auditActionFilter, setAuditActionFilter] = useState("all");
  // Cars sub-tab numerical sort: by car number, by unit number, or by current post number.
  const [carSort, setCarSort] = useState("car");
  // Free-text search across the cars grid — matches car #, unit #, role.
  const [carSearch, setCarSearch] = useState("");
  // Car-number whose "all posts" dialog is open (null = closed).
  const [allPostsCarNumber, setAllPostsCarNumber] = useState(null);
  // Local sequence inside the "all posts" dialog so long-press drag can
  // reorder optimistically; commits via /cars/{n}/reorder on drop.
  const [allPostsSeq, setAllPostsSeq] = useState([]);
  const [dragArmedIdx, setDragArmedIdx] = useState(null);
  const [dragHoverIdx, setDragHoverIdx] = useState(null);
  const longPressTimerRef = useRef(null);
  const dragRowRefs = useRef([]);
  // Text-edit mode: type the sequence as "3, 5, 4" instead of dragging.
  const [allPostsTextEdit, setAllPostsTextEdit] = useState(false);
  const [allPostsText, setAllPostsText] = useState("");
  // Reset / seed the dialog state whenever it opens for a new car.
  useEffect(() => {
    if (allPostsCarNumber == null) {
      setAllPostsSeq([]);
      setDragArmedIdx(null);
      setDragHoverIdx(null);
      setAllPostsTextEdit(false);
      setAllPostsText("");
      if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
      return;
    }
    const carObj = cars.find(c => String(c.car_number) === String(allPostsCarNumber));
    setAllPostsSeq(carObj?.post_sequence ? [...carObj.post_sequence] : []);
  }, [allPostsCarNumber, cars]);
  const handleReorderRowDown = (idx) => (e) => {
    if (e.button !== undefined && e.button !== 0) return; // left button / touch only
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      setDragArmedIdx(idx);
      setDragHoverIdx(idx);
      longPressTimerRef.current = null;
    }, 1000);
  };
  const handleReorderRowMove = (e) => {
    // Cancel long-press if user moved before it armed (intentional touch wiggle)
    if (longPressTimerRef.current && dragArmedIdx === null) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
      return;
    }
    if (dragArmedIdx === null) return;
    const y = e.clientY;
    const target = dragRowRefs.current.findIndex(ref => {
      if (!ref) return false;
      const r = ref.getBoundingClientRect();
      return y >= r.top && y <= r.bottom;
    });
    if (target >= 0) setDragHoverIdx(target);
  };
  const handleReorderRowUp = async () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    const from = dragArmedIdx;
    const to = dragHoverIdx;
    setDragArmedIdx(null);
    setDragHoverIdx(null);
    if (from == null || to == null || from === to) return;
    const next = [...allPostsSeq];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setAllPostsSeq(next);
    try {
      await ax.put(
        `${API}/event-ops/events/${activeEvent.event_id}/cars/${encodeURIComponent(allPostsCarNumber)}/reorder`,
        { post_sequence: next }
      );
      fetchEventData(activeEvent.event_id);
    } catch (e) {
      toast.error("Failed to reorder");
      // Revert on failure by re-reading from props
      const carObj = cars.find(c => String(c.car_number) === String(allPostsCarNumber));
      setAllPostsSeq(carObj?.post_sequence ? [...carObj.post_sequence] : []);
    }
  };

  // Shared destructive-action confirm dialog. Populate via askConfirm()
  // and the AlertDialog at the bottom of the page renders it.
  const [confirmAction, setConfirmAction] = useState(null);
  const askConfirm = (opts) => setConfirmAction(opts);
  // Real-time clock (matches LiveCalls header).
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // WhatsApp bot groups for the ring-alert group picker. Endpoint is
  // admin-only on the backend; non-admin users with dispatch access
  // simply get an empty list and the UI shows the "configure WhatsApp"
  // hint instead of the list.
  const [botGroups, setBotGroups] = useState([]);
  useEffect(() => {
    if (!isAdmin) return;
    ax.get(`${API}/audit/whatsapp/bot-groups`)
      .then((r) => setBotGroups(Array.isArray(r.data?.groups) ? r.data.groups : []))
      .catch(() => {});
  }, [isAdmin]);

  // Ring popup — fires when a post.ring_sent_at is set and the same
  // post doesn't yet have ring_acknowledged_at. Acknowledge POSTs to
  // /ring-acknowledge so the dismissal lives in MongoDB and persists
  // across refreshes, tabs, and devices. (Earlier localStorage version
  // still re-popped on some setups — backend state is bulletproof.)
  const [activeRing, setActiveRing] = useState(null);
  useEffect(() => {
    if (!posts || posts.length === 0 || !activeEvent?.event_id) return;
    const due = posts.find(p =>
      p.ring_enabled && p.ring_sent_at && !p.ring_acknowledged_at
    );
    if (due && (!activeRing || activeRing.post_number !== due.post_number)) {
      setActiveRing(due);
    }
  }, [posts, activeEvent?.event_id, activeRing]);
  const dismissRing = async () => {
    if (!activeRing || !activeEvent?.event_id) { setActiveRing(null); return; }
    const post = activeRing;
    setActiveRing(null);
    try {
      await ax.post(`${API}/event-ops/events/${activeEvent.event_id}/posts/${post.post_number}/ring-acknowledge`);
      fetchEventData(activeEvent.event_id);
    } catch (e) {
      toast.error("Failed to save acknowledgement");
    }
  };

  // Dispatch vs Unit mode selection. Determined from per-user permission
  // flags set in Settings: event_ops_dispatch_access / event_ops_unit_access.
  // null = not yet decided (modal will prompt if user has both).
  const [mode, setMode] = useState(null);
  const canDispatch = !!(user?.event_ops_dispatch_access || isAdmin);
  const canUnit = !!(user?.event_ops_unit_access || isAdmin);
  // Auto-pick when only one access is granted; otherwise keep null and the
  // mode-picker dialog will fire on render.
  useEffect(() => {
    if (mode) return;
    if (canDispatch && !canUnit) setMode("dispatch");
    else if (canUnit && !canDispatch) setMode("unit");
  }, [mode, canDispatch, canUnit]);

  // polling
  const pollingRef = useRef(null);

  const isLive = activeEvent?.status === "live";

  // In Unit mode, auto-select the live event (unit users don't browse the
  // event list — they only care about whatever is currently running).
  useEffect(() => {
    if (mode !== "unit" || activeEvent) return;
    const live = (events || []).find(e => e.status === "live");
    if (live) setActiveEvent(live);
  }, [mode, activeEvent, events]);

  // ─── data fetching ─────────────────────────────────────────────────
  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await ax.get(`${API}/event-ops/events`);
      setEvents(data.events || data || []);
    } catch (e) {
      console.error("Failed to fetch events", e);
    }
  }, []);

  const fetchEventData = useCallback(async (eventId) => {
    if (!eventId) return;
    try {
      const [postsRes, carsRes, schoolsRes, closuresRes, routesRes, updatesRes] = await Promise.all([
        ax.get(`${API}/event-ops/events/${eventId}/posts`),
        ax.get(`${API}/event-ops/events/${eventId}/cars`),
        ax.get(`${API}/event-ops/events/${eventId}/schools`),
        ax.get(`${API}/event-ops/events/${eventId}/closures`),
        ax.get(`${API}/event-ops/events/${eventId}/routes`).catch(() => ({ data: [] })),
        ax.get(`${API}/event-ops/events/${eventId}/updates`).catch(() => ({ data: [] })),
      ]);
      setPosts(postsRes.data.posts || postsRes.data || []);
      setCars(carsRes.data.cars || carsRes.data || []);
      setSchools(schoolsRes.data.schools || schoolsRes.data || []);
      setClosures(closuresRes.data.closures || closuresRes.data || []);
      const fetchedRoutes = routesRes.data.routes || routesRes.data || [];
      setRoutes(fetchedRoutes);
      setUpdates(Array.isArray(updatesRes.data) ? updatesRes.data : (updatesRes.data?.updates || []));
      setVisibleRouteIds((prev) => {
        // On first load, show all. On refresh, keep existing selection.
        if (prev.size === 0) return new Set(fetchedRoutes.map(r => r.route_id));
        return prev;
      });
    } catch (e) {
      console.error("Failed to fetch event data", e);
    }
  }, []);

  const fetchAudit = useCallback(async (eventId) => {
    if (!eventId) return;
    try {
      const { data } = await ax.get(`${API}/event-ops/events/${eventId}/audit`);
      setAudit(Array.isArray(data) ? data : Array.isArray(data?.entries) ? data.entries : []);
    } catch (e) {
      console.error("Failed to fetch audit", e);
    }
  }, []);

  // initial load
  useEffect(() => {
    fetchEvents();
    // Fetch app units for car assignment dropdown
    (async () => {
      try {
        const { data } = await axios.get(`${API}/units`, { withCredentials: true });
        setAppUnits(Array.isArray(data) ? data : data.units || []);
      } catch {}
    })();
  }, [fetchEvents]);

  // when active event changes, load its data
  useEffect(() => {
    if (activeEvent?.event_id) {
      fetchEventData(activeEvent.event_id);
      if (activeTab === "audit") fetchAudit(activeEvent.event_id);
    }
  }, [activeEvent?.event_id, fetchEventData, fetchAudit, activeTab]);

  // live polling
  useEffect(() => {
    if (isLive && activeEvent?.event_id) {
      pollingRef.current = setInterval(() => {
        fetchEventData(activeEvent.event_id);
        if (activeTab === "audit") fetchAudit(activeEvent.event_id);
      }, 5000);
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [isLive, activeEvent?.event_id, fetchEventData, fetchAudit, activeTab]);

  // ─── actions ───────────────────────────────────────────────────────
  const selectEvent = (eventId) => {
    const ev = events.find((e) => String(e.event_id) === String(eventId));
    setActiveEvent(ev || null);
  };

  const createEvent = async () => {
    try {
      const { data } = await ax.post(`${API}/event-ops/events`, {
        name: newEventForm.name,
        date: newEventForm.date,
        copy_from_event_id: newEventForm.copy_from_event_id || undefined,
      });
      toast.success("Event created");
      setShowCreateEvent(false);
      setNewEventForm({ name: "", date: "", copy_from_event_id: "" });
      await fetchEvents();
      const created = data.event || data;
      if (created?.event_id) setActiveEvent(created);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to create event");
    }
  };


  const goLive = async () => {
    try {
      const { data } = await ax.put(`${API}/event-ops/events/${activeEvent.event_id}/status`, { status: "live" });
      toast.success("Event is now LIVE");
      setShowGoLiveConfirm(false);
      setActiveEvent(data);
      fetchEvents();
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to go live");
    }
  };

  const completeEvent = async () => {
    try {
      const { data } = await ax.put(`${API}/event-ops/events/${activeEvent.event_id}/status`, { status: "completed" });
      toast.success("Event completed");
      setShowCompleteConfirm(false);
      setActiveEvent(data);
      fetchEvents();
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to complete event");
    }
  };

  // Post CRUD
  const savePost = async (postData) => {
    try {
      const { _isEdit, ...data } = postData;
      if (_isEdit) {
        await ax.put(`${API}/event-ops/events/${activeEvent.event_id}/posts/${data.post_number}`, data);
        toast.success("Post updated");
      } else {
        await ax.post(`${API}/event-ops/events/${activeEvent.event_id}/posts`, data);
        toast.success("Post created");
      }
      setShowEditPost(false);
      setEditingPost(null);
      fetchEventData(activeEvent.event_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.error || "Failed to save post");
    }
  };

  // ── Cars export — produces the dispatch text format ─────────────
  // "Car {n}: {unit_or_n}\n  {location} ({position})\n..."
  // Position field appended in parens when present (e.g. "(assist)").
  const buildCarExportLine = (post) => {
    const loc = (post?.location || "").trim();
    const pos = (post?.position || "").trim();
    if (loc && pos) return `${loc} (${pos})`;
    return loc || (post?.specific || "").trim() || `Post #${post?.post_number ?? "?"}`;
  };
  const buildCarExport = (car) => {
    const headerId = car.unit_number ? car.unit_number : `Car ${car.car_number}`;
    const lines = [`Car ${car.car_number}: ${headerId}`];
    for (const pn of (car.post_sequence || [])) {
      const p = posts.find((pp) => String(pp.post_number) === String(pn));
      if (p) lines.push(buildCarExportLine(p));
      else lines.push(`Post #${pn}`);
    }
    return lines.join("\n");
  };
  const buildAllCarsExport = () => sortedCars.map(buildCarExport).join("\n\n");

  // ── Updates (manual notes) ───────────────────────────────────────
  const saveUpdate = async (data) => {
    try {
      await ax.post(`${API}/event-ops/events/${activeEvent.event_id}/updates`, data);
      toast.success("Update added");
      setShowAddUpdate(false);
      fetchEventData(activeEvent.event_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add update");
    }
  };
  const deleteUpdate = (updateId) => askConfirm({
    title: "Delete this update?",
    description: "This can't be undone.",
    confirmLabel: "Delete",
    destructive: true,
    onConfirm: async () => {
      try {
        await ax.delete(`${API}/event-ops/events/${activeEvent.event_id}/updates/${updateId}`);
        toast.success("Update deleted");
        fetchEventData(activeEvent.event_id);
      } catch (e) {
        toast.error("Failed to delete update");
      }
    },
  });

  const deletePost = (postId) => askConfirm({
    title: `Delete post #${postId}?`,
    description: "This can't be undone.",
    confirmLabel: "Delete",
    destructive: true,
    onConfirm: async () => {
      try {
        await ax.delete(`${API}/event-ops/events/${activeEvent.event_id}/posts/${postId}`);
        toast.success("Post deleted");
        fetchEventData(activeEvent.event_id);
      } catch (e) {
        toast.error(e.response?.data?.detail || e.response?.data?.error || "Failed to delete post");
      }
    },
  });

  const reassignPostCar = async (postNumber, newCar) => {
    try {
      await ax.post(`${API}/event-ops/events/${activeEvent.event_id}/posts/${postNumber}/reassign`, { assigned_car: newCar });
      toast.success(`Post #${postNumber} reassigned to ${newCar || "none"}`);
      fetchEventData(activeEvent.event_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reassign failed");
    }
  };

  // Status-change wrapper — when the post has no car/unit assigned, ask
  // confirmation before firing the action. Shared by every status path
  // (Posts list, Map, viewingPost dialog, etc.) so unassigned posts can't
  // silently flip status by mistake.
  const runPostStatusAction = (postId, label, fn) => {
    const post = posts.find(p => String(p.post_number) === String(postId));
    if (post && !post.assigned_car) {
      askConfirm({
        title: "No car assigned",
        description: `Post #${postId} has no car or unit assigned. Mark it ${label} anyway?`,
        confirmLabel: `Mark ${label}`,
        onConfirm: fn,
      });
      return;
    }
    fn();
  };

  const _enroutePost = async (postId) => {
    try {
      await ax.post(`${API}/event-ops/events/${activeEvent.event_id}/posts/${postId}/enroute`);
      toast.success("Unit en route");
      fetchEventData(activeEvent.event_id);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to set en route");
    }
  };
  // Going en route gets two extra guards on top of the no-car check:
  //   1) Same as other actions — if no car assigned, ask first.
  //   2) If the assigned car already has another post in en route or
  //      covered status, ask before yanking attention to a new one.
  const enroutePost = (postId) => {
    const post = posts.find(p => String(p.post_number) === String(postId));
    const fire = () => _enroutePost(postId);
    if (post && !post.assigned_car) {
      askConfirm({
        title: "No car assigned",
        description: `Post #${postId} has no car or unit assigned. Mark it en route anyway?`,
        confirmLabel: "Mark en route",
        onConfirm: fire,
      });
      return;
    }
    if (post?.assigned_car) {
      const conflicting = posts.filter(p =>
        String(p.assigned_car) === String(post.assigned_car) &&
        String(p.post_number) !== String(post.post_number) &&
        (p.status === "enroute" || p.status === "covered")
      );
      if (conflicting.length > 0) {
        const labels = conflicting
          .map(p => `Post #${p.post_number} (${p.status === "enroute" ? "en route" : "covered"})`)
          .join(", ");
        askConfirm({
          title: `Car ${post.assigned_car} already active`,
          description: `${labels}. Mark Post #${postId} en route anyway? It will become this car's current post.`,
          confirmLabel: "Mark en route",
          onConfirm: fire,
        });
        return;
      }
    }
    fire();
  };

  const _checkInPost = async (postId) => {
    try {
      await ax.post(`${API}/event-ops/events/${activeEvent.event_id}/posts/${postId}/check-in`);
      toast.success("Post checked in");
      fetchEventData(activeEvent.event_id);
    } catch (e) {
      toast.error(e.response?.data?.error || "Check-in failed");
    }
  };
  const checkInPost = (postId) => runPostStatusAction(postId, "covered", () => _checkInPost(postId));

  const _openPost = async (postId) => {
    try {
      await ax.post(`${API}/event-ops/events/${activeEvent.event_id}/posts/${postId}/open`);
      toast.success("Post opened");
      fetchEventData(activeEvent.event_id);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to open post");
    }
  };
  const openPost = (postId) => runPostStatusAction(postId, "open", () => _openPost(postId));

  const _clearPost = async (postId) => {
    try {
      await ax.post(`${API}/event-ops/events/${activeEvent.event_id}/posts/${postId}/clear`);
      toast.success("Post cleared");
      fetchEventData(activeEvent.event_id);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to clear post");
    }
  };
  const clearPost = (postId) => runPostStatusAction(postId, "cleared", () => _clearPost(postId));

  // Reset a post back to "pending". There's no dedicated endpoint, so we
  // use the generic PUT that updates any post field. Without this, the
  // status dropdown's "pending" option used to call openPost, which
  // immediately flipped the post to "open" (the user-visible bug).
  const _pendingPost = async (postId) => {
    try {
      await ax.put(`${API}/event-ops/events/${activeEvent.event_id}/posts/${postId}`, { status: "pending" });
      toast.success("Post reset to pending");
      fetchEventData(activeEvent.event_id);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to reset to pending");
    }
  };
  const pendingPost = (postId) => runPostStatusAction(postId, "pending", () => _pendingPost(postId));

  // Car CRUD
  const saveCar = async (carData) => {
    const doSave = async () => {
      try {
        if (carData._isEdit) {
          const { _isEdit, ...data } = carData;
          await ax.put(`${API}/event-ops/events/${activeEvent.event_id}/cars/${encodeURIComponent(data.car_number)}`, data);
          toast.success("Car updated");
        } else {
          const { _isEdit, ...data } = carData;
          await ax.post(`${API}/event-ops/events/${activeEvent.event_id}/cars`, data || carData);
          toast.success("Car created");
        }
        setShowEditCar(false);
        setEditingCar(null);
        fetchEventData(activeEvent.event_id);
      } catch (e) {
        toast.error(e.response?.data?.error || "Failed to save car");
      }
    };
    // One-car-per-unit guardrail: if this unit is already on a different
    // car in the event, require an explicit override before reassigning.
    const newUnit = (carData.unit_number || "").trim();
    if (newUnit) {
      const conflict = cars.find(c =>
        String(c.unit_number || "") === newUnit &&
        String(c.car_number) !== String(carData.car_number)
      );
      if (conflict) {
        askConfirm({
          title: `Put ${newUnit} on both cars?`,
          description: `${newUnit} is already on Car ${conflict.car_number}. Confirming will leave ${newUnit} on Car ${conflict.car_number} AND also assign them to Car ${carData.car_number || "(new)"}. The unit will be on both cars.`,
          confirmLabel: "Yes, put on both",
          onConfirm: doSave,
        });
        return;
      }
    }
    await doSave();
  };

  const deleteCar = (carId) => askConfirm({
    title: `Delete car ${carId}?`,
    description: "This can't be undone.",
    confirmLabel: "Delete",
    destructive: true,
    onConfirm: async () => {
      try {
        await ax.delete(`${API}/event-ops/events/${activeEvent.event_id}/cars/${carId}`);
        toast.success("Car deleted");
        fetchEventData(activeEvent.event_id);
      } catch (e) {
        toast.error(e.response?.data?.error || "Failed to delete car");
      }
    },
  });

  const moveCarNext = async (carId) => {
    try {
      await ax.post(`${API}/event-ops/events/${activeEvent.event_id}/cars/${carId}/next`);
      toast.success("Car moved to next post");
      fetchEventData(activeEvent.event_id);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to advance car");
    }
  };

  // Save the per-event unit roster (list of unit names). Independent of
  // cars — the roster only controls which units appear in the Unit
  // Roster dialog. Whether each is "Available" or "Car X" is still
  // derived from `cars`.
  const saveUnitRoster = async (names) => {
    try {
      const { data } = await ax.put(
        `${API}/event-ops/events/${activeEvent.event_id}`,
        { unit_roster: names },
      );
      setActiveEvent(data);
      toast.success("Roster saved");
      setShowUnitRosterEdit(false);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to save roster");
    }
  };

  // School CRUD
  const saveSchool = async (schoolData) => {
    try {
      const { _isEdit, ...data } = schoolData;
      if (_isEdit && data.school_id) {
        await ax.put(`${API}/event-ops/events/${activeEvent.event_id}/schools/${encodeURIComponent(data.school_id)}`, data);
        toast.success("School updated");
      } else {
        await ax.post(`${API}/event-ops/events/${activeEvent.event_id}/schools`, data);
        toast.success("School created");
      }
      setShowEditSchool(false);
      setEditingSchool(null);
      fetchEventData(activeEvent.event_id);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to save school");
    }
  };

  const deleteSchool = (schoolId) => askConfirm({
    title: "Delete this school?",
    description: "This can't be undone.",
    confirmLabel: "Delete",
    destructive: true,
    onConfirm: async () => {
      try {
        await ax.delete(`${API}/event-ops/events/${activeEvent.event_id}/schools/${schoolId}`);
        toast.success("School deleted");
        fetchEventData(activeEvent.event_id);
      } catch (e) {
        toast.error(e.response?.data?.error || "Failed to delete school");
      }
    },
  });

  // Closure CRUD
  const saveClosure = async (closureData) => {
    try {
      const { _isEdit, ...data } = closureData;
      if (_isEdit && data.closure_id) {
        await ax.put(`${API}/event-ops/events/${activeEvent.event_id}/closures/${encodeURIComponent(data.closure_id)}`, data);
        toast.success("Closure updated");
      } else {
        await ax.post(`${API}/event-ops/events/${activeEvent.event_id}/closures`, data);
        toast.success("Closure created");
      }
      setShowEditClosure(false);
      setEditingClosure(null);
      fetchEventData(activeEvent.event_id);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to save closure");
    }
  };

  const deleteClosure = (closureId) => askConfirm({
    title: "Delete this closure?",
    description: "This can't be undone.",
    confirmLabel: "Delete",
    destructive: true,
    onConfirm: async () => {
      try {
        await ax.delete(`${API}/event-ops/events/${activeEvent.event_id}/closures/${encodeURIComponent(closureId)}`);
        toast.success("Closure deleted");
        fetchEventData(activeEvent.event_id);
      } catch (e) {
        toast.error(e.response?.data?.error || "Failed to delete closure");
      }
    },
  });

  // Route CRUD
  const saveRoute = async (routeData) => {
    try {
      const { _isEdit, ...data } = routeData;
      if (_isEdit && data.route_id) {
        await ax.put(`${API}/event-ops/events/${activeEvent.event_id}/routes/${encodeURIComponent(data.route_id)}`, data);
        toast.success("Route updated");
      } else {
        await ax.post(`${API}/event-ops/events/${activeEvent.event_id}/routes`, data);
        toast.success("Route created");
      }
      setShowEditRoute(false);
      setEditingRoute(null);
      fetchEventData(activeEvent.event_id);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to save route");
    }
  };

  const deleteRoute = (routeId) => askConfirm({
    title: "Delete this route?",
    description: "This can't be undone.",
    confirmLabel: "Delete",
    destructive: true,
    onConfirm: async () => {
      try {
        await ax.delete(`${API}/event-ops/events/${activeEvent.event_id}/routes/${encodeURIComponent(routeId)}`);
        toast.success("Route deleted");
        fetchEventData(activeEvent.event_id);
      } catch (e) {
        toast.error(e.response?.data?.error || "Failed to delete route");
      }
    },
  });

  const updateLeadTail = async (routeId, type, lat, lng) => {
    try {
      await ax.put(`${API}/event-ops/events/${activeEvent.event_id}/routes/${encodeURIComponent(routeId)}/${type}`, { lat, lng });
      toast.success(`${type === "lead" ? "Lead" : "Tail"} position updated`);
      setShowLeadTailDialog(false);
      setLeadTailTarget(null);
      fetchEventData(activeEvent.event_id);
    } catch (e) {
      toast.error(e.response?.data?.error || `Failed to update ${type}`);
    }
  };

  // ─── computed ──────────────────────────────────────────────────────
  const postCounts = {
    covered: posts.filter((p) => p.status === "covered").length,
    enroute: posts.filter((p) => p.status === "enroute").length,
    open: posts.filter((p) => p.status === "open").length,
    pending: posts.filter((p) => p.status === "pending").length,
    cleared: posts.filter((p) => p.status === "cleared").length,
    total: posts.length,
    unassigned: posts.filter((p) => !p.assigned_car).length,
  };

  const carCounts = {
    posted: cars.filter((c) => c.status === "posted").length,
    en_route: cars.filter((c) => c.status === "en_route").length,
    standby: cars.filter((c) => c.status === "standby").length,
    done: cars.filter((c) => c.status === "done").length,
    total: cars.length,
  };

  const filteredPosts = posts.filter((p) => {
    if (postStatusFilter !== "all" && p.status !== postStatusFilter) return false;
    if (postCarFilter !== "all" && String(p.assigned_car) !== postCarFilter) return false;
    if (postRouteFilter === "__none__") {
      if (postRouteIds(p).length > 0) return false;
    } else if (postRouteFilter !== "all") {
      if (!postRouteIds(p).includes(String(postRouteFilter))) return false;
    }
    if (postSearchNumber.trim()) {
      const q = postSearchNumber.trim().toLowerCase();
      if (!String(p.post_number ?? "").toLowerCase().includes(q)) return false;
    }
    if (postSearchUnit.trim()) {
      const q = postSearchUnit.trim().toLowerCase();
      // Match the post's own unit, OR the unit on the car this post is
      // assigned to (the post's assigned_unit can lag the car's unit if
      // the car was edited after the post was attached).
      const carForPost = cars.find(c => String(c.car_number) === String(p.assigned_car));
      const unit = [p.assigned_unit, p.unit, carForPost?.unit_number]
        .map(v => String(v ?? "").toLowerCase())
        .join(" ");
      if (!unit.includes(q)) return false;
    }
    if (postSearchAny.trim()) {
      const q = postSearchAny.trim().toLowerCase();
      const hay = [
        p.post_number, p.location, p.specific, p.position, p.notes,
        p.assigned_car, p.assigned_unit, p.unit, p.priority,
      ].map(v => String(v ?? "").toLowerCase()).join(" ");
      if (!hay.includes(q)) return false;
    }
    return true;
  }).sort((a, b) => {
    if (postSortMode === "custom") {
      // Use the event's predefined order if set; posts not in the order
      // go to the end (numerically sorted among themselves).
      const order = (activeEvent?.posts_custom_order || []).map(String);
      const ai = order.indexOf(String(a.post_number));
      const bi = order.indexOf(String(b.post_number));
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      // Both unranked — fall through to numeric.
    } else if (postSortMode === "ring") {
      // Ring-scheduled posts ascending by ring_at; ones without a
      // ring scheduled fall to the end (then numerically sorted).
      const aHas = a.ring_enabled && a.ring_at;
      const bHas = b.ring_enabled && b.ring_at;
      if (aHas && bHas) {
        const at = new Date(a.ring_at).getTime();
        const bt = new Date(b.ring_at).getTime();
        if (at !== bt) return at - bt;
      } else if (aHas) {
        return -1;
      } else if (bHas) {
        return 1;
      }
      // Both unringed — fall through to numeric.
    } else if (postSortMode === "priority") {
      // Priority highest first (critical → high → medium → low → none).
      const rank = { critical: 4, high: 3, medium: 2, low: 1 };
      const ar = rank[a.priority] || 0;
      const br = rank[b.priority] || 0;
      if (ar !== br) return br - ar;
      // Same priority — fall through to numeric.
    } else if (postSortMode === "car") {
      // Group by assigned_car, sorted by car # numerically; within a
      // car, follow that car's saved post_sequence so posts read in
      // dispatch order. Posts without a car go to the end.
      const aCar = String(a.assigned_car ?? "");
      const bCar = String(b.assigned_car ?? "");
      if (aCar !== bCar) {
        if (!aCar) return 1;
        if (!bCar) return -1;
        const an = parseInt(aCar, 10), bn = parseInt(bCar, 10);
        if (!isNaN(an) && !isNaN(bn)) return an - bn;
        return aCar.localeCompare(bCar);
      }
      // Same car — order by sequence index.
      if (aCar) {
        const car = cars.find(c => String(c.car_number) === aCar);
        const seq = (car?.post_sequence || []).map(String);
        const ai = seq.indexOf(String(a.post_number));
        const bi = seq.indexOf(String(b.post_number));
        if (ai !== -1 && bi !== -1) return ai - bi;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
      }
      // Fall through to numeric for siblings outside any sequence.
    }
    const an = parseInt(a.post_number, 10);
    const bn = parseInt(b.post_number, 10);
    if (!isNaN(an) && !isNaN(bn)) return an - bn;
    return String(a.post_number).localeCompare(String(b.post_number));
  });

  // Cars sub-tab — numerical sort by car number, unit number, or current post.
  // Helper: extract digits from any string ("CH86" → 86, "Car 12" → 12).
  // Cars/units without a numeric component sort to the end (Infinity).
  const numFrom = (val) => {
    if (val == null) return Infinity;
    const m = String(val).match(/-?\d+/);
    return m ? parseInt(m[0], 10) : Infinity;
  };
  const sortedCars = [...cars]
    .filter((c) => {
      const q = carSearch.trim().toLowerCase();
      if (!q) return true;
      const hay = [c.car_number, c.unit_number, c.role]
        .map((v) => String(v ?? "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    })
    .sort((a, b) => {
      if (carSort === "unit") return numFrom(a.unit_number) - numFrom(b.unit_number);
      if (carSort === "post") {
        const ap = a.current_post != null ? a.current_post : (a.post_sequence?.[0]);
        const bp = b.current_post != null ? b.current_post : (b.post_sequence?.[0]);
        return numFrom(ap) - numFrom(bp);
      }
      return numFrom(a.car_number) - numFrom(b.car_number);
    });

  const filteredAudit = (audit || []).filter((a) => {
    if (auditActionFilter !== "all" && a.action !== auditActionFilter) return false;
    return true;
  });

  // ─── render ────────────────────────────────────────────────────────

  // Mode picker overlay — shown when the user can do both and hasn't picked.
  // Stays rendered even after a choice so a "Switch View" button can re-open it.
  const modePicker = (
    <Dialog open={mode === null && canDispatch && canUnit}
            onOpenChange={(open) => { if (!open) setMode("dispatch"); }}>
      <DialogContent aria-describedby={undefined} className="max-w-sm">
        <DialogHeader><DialogTitle>Open Event Ops as…</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-3 mt-2">
          <Button size="lg" onClick={() => setMode("dispatch")}>
            <Shield className="h-4 w-4 mr-2" /> Dispatch (full)
          </Button>
          <Button size="lg" variant="outline" onClick={() => { setActiveEvent(null); setMode("unit"); }}>
            <Car className="h-4 w-4 mr-2" /> Unit (my car only)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  // No access at all — gate at component level (ProtectedRoute may be lax).
  if (!canDispatch && !canUnit) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">You don't have access to Event Ops. Ask an admin to grant Dispatch or Unit access.</p>
      </div>
    );
  }

  // Unit-view path — separate, focused page.
  if (mode === "unit") {
    return (
      <>
        {modePicker}
        <UnitView
          user={user}
          activeEvent={activeEvent}
          events={events}
          cars={cars}
          posts={posts}
          routes={routes}
          viewingPost={viewingPost}
          setViewingPost={setViewingPost}
          canEdit={!!(user?.event_ops_unit_can_edit || isAdmin)}
          canSwitch={canDispatch && canUnit}
          onSwitchView={() => setMode(null)}
          actions={{ enroutePost, checkInPost, openPost, clearPost, pendingPost }}
        />
      </>
    );
  }

  if (!activeEvent) {
    // No event selected — show setup / selection screen
    return (
      <div className="p-4 md:p-6 space-y-6">
        {modePicker}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6" /> Event Operations
          </h1>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground font-mono sm:hidden">
              {currentTime.toLocaleString('en-US', { timeZone: 'America/New_York', hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
            <span className="text-base text-muted-foreground font-mono hidden sm:block">
              {currentTime.toLocaleString('en-US', { timeZone: 'America/New_York', hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
            {canDispatch && canUnit && (
              <Button variant="outline" size="sm" onClick={() => setMode(null)}>
                Switch View
              </Button>
            )}
            <Button onClick={() => setShowCreateEvent(true)}>
              <Plus className="h-4 w-4 mr-1" /> New Event
            </Button>
          </div>
        </div>

        {/* Event list */}
        <Card>
          <CardHeader><CardTitle>Events</CardTitle></CardHeader>
          <CardContent>
            {events.length === 0 ? (
              <p className="text-muted-foreground text-sm">No events yet. Create one to get started.</p>
            ) : (
              <div className="space-y-2">
                {events.map((ev) => (
                  <div
                    key={ev.event_id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer"
                    onClick={() => setActiveEvent(ev)}
                  >
                    <div>
                      <span className="font-medium">{ev.name}</span>
                      <span className="text-muted-foreground text-sm ml-2">{ev.date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={ev.status === "live" ? "destructive" : ev.status === "completed" ? "secondary" : "outline"}>
                        {ev.status || "plan"}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create event dialog */}
        <Dialog open={showCreateEvent} onOpenChange={setShowCreateEvent}>
          <DialogContent aria-describedby={undefined}>
            <DialogHeader><DialogTitle>Create Event</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input value={newEventForm.name} onChange={(e) => setNewEventForm({ ...newEventForm, name: e.target.value })} placeholder="Parade 2026" />
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={newEventForm.date} onChange={(e) => setNewEventForm({ ...newEventForm, date: e.target.value })} />
              </div>
              <div>
                <Label>Copy from existing event (optional)</Label>
                <Select value={newEventForm.copy_from_event_id || "none"} onValueChange={(v) => setNewEventForm({ ...newEventForm, copy_from_event_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="No event" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {events.map((ev) => (
                      <SelectItem key={ev.event_id} value={String(ev.event_id)}>{ev.name}{ev.date ? ` (${ev.date})` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={createEvent} disabled={!newEventForm.name}>Create Event</Button>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    );
  }

  // ─── Event selected — show tabs ────────────────────────────────────
  return (
    <div className="p-4 md:p-6 space-y-4">
      {modePicker}
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <div className="flex items-center gap-3 flex-wrap min-w-0">
          <Button variant="ghost" size="sm" onClick={() => setActiveEvent(null)}>
            &larr; Back
          </Button>
          <h1 className="text-xl font-bold truncate">{activeEvent.name}</h1>
          <Badge variant={isLive ? "destructive" : activeEvent.status === "completed" ? "secondary" : "outline"}>
            {isLive ? "LIVE" : activeEvent.status || "Plan"}
          </Badge>
          {isLive && <span className="text-xs text-muted-foreground animate-pulse">Auto-refresh 5s</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground font-mono sm:hidden">
            {currentTime.toLocaleString('en-US', { timeZone: 'America/New_York', hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
          <span className="text-base text-muted-foreground font-mono hidden sm:block">
            {currentTime.toLocaleString('en-US', { timeZone: 'America/New_York', hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
          {canDispatch && canUnit && (
            <Button variant="outline" size="sm" onClick={() => setMode(null)}>
              Switch View
            </Button>
          )}
          {activeEvent.status !== "completed" && (
            <>
              {!isLive && canDispatch && (
                <Button variant="destructive" onClick={() => setShowGoLiveConfirm(true)}>
                  <Play className="h-4 w-4 mr-1" /> Go Live
                </Button>
              )}
              {isLive && canDispatch && (
                <>
                  <Button variant="ghost" size="sm" onClick={async () => {
                    if (!window.confirm("Switch back to Plan mode? Live tracking will pause.")) return;
                    try {
                      const { data } = await ax.put(`${API}/event-ops/events/${activeEvent.event_id}/status`, { status: "planning" });
                      setActiveEvent(data);
                      fetchEvents();
                      toast.success("Switched to Plan mode");
                    } catch (e) { toast.error("Failed to switch"); }
                  }}>
                    <Edit className="h-4 w-4 mr-1" /> Plan Mode
                  </Button>
                  <Button variant="outline" onClick={() => setShowCompleteConfirm(true)}>
                    <Square className="h-4 w-4 mr-1" /> Complete Event
                  </Button>
                </>
              )}
            </>
          )}
          {activeEvent.status === "completed" && canDispatch && (
            <Button variant="outline" size="sm" onClick={async () => {
              if (!window.confirm("Reopen this event? It will go back to Plan mode.")) return;
              try {
                const { data } = await ax.put(`${API}/event-ops/events/${activeEvent.event_id}/status`, { status: "planning" });
                setActiveEvent(data);
                fetchEvents();
                toast.success("Event reopened");
              } catch (e) { toast.error("Failed to reopen"); }
            }}>
              <Play className="h-4 w-4 mr-1" /> Reopen
            </Button>
          )}
          {canDispatch && (
            <Button variant="outline" size="sm" onClick={async () => {
              const name = window.prompt("Name for the copy:", `${activeEvent.name} (copy)`);
              if (!name) return;
              try {
                const { data } = await ax.post(`${API}/event-ops/events`, { name, date: activeEvent.date || "", copy_from_event_id: activeEvent.event_id });
                toast.success("Event copied");
                await fetchEvents();
                const created = data.event || data;
                if (created?.event_id) setActiveEvent(created);
              } catch (e) { toast.error("Failed to copy"); }
            }}>
              <Plus className="h-4 w-4 mr-1" /> Copy Event
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => { fetchEventData(activeEvent.event_id); if (activeTab === "audit") fetchAudit(activeEvent.event_id); }}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === "audit" && activeEvent?.event_id) fetchAudit(activeEvent.event_id); }}>
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="posts">Posts</TabsTrigger>
          <TabsTrigger value="schools">Schools</TabsTrigger>
          <TabsTrigger value="closures">Closures</TabsTrigger>
          <TabsTrigger value="routes">Routes</TabsTrigger>
          {isAdmin && <TabsTrigger value="audit">Audit</TabsTrigger>}
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          {!isLive && activeEvent.status !== "completed" && <TabsTrigger value="setup">Setup</TabsTrigger>}
        </TabsList>

        {/* ── Dashboard ─────────────────────────────────────────────── */}
        <TabsContent value="dashboard">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total Posts" value={postCounts.total} icon={MapPin} />
            <StatCard label={isLive ? "Covered" : "Assigned"} value={isLive ? postCounts.covered : postCounts.total - postCounts.unassigned} icon={CheckCircle} color="text-green-600" />
            <StatCard label={isLive ? "Open" : "Unassigned"} value={isLive ? postCounts.open : postCounts.unassigned} icon={XCircle} color="text-red-600" />
            <StatCard label={isLive ? "Cleared" : "Schools"} value={isLive ? postCounts.cleared : schools.length} icon={isLive ? Clock : School} color="text-blue-600" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <StatCard label="Total Cars" value={carCounts.total} icon={Car} />
            <StatCard label={isLive ? "Posted" : "Cars Ready"} value={isLive ? carCounts.posted : carCounts.total} icon={Car} color="text-green-600" />
            <StatCard label={isLive ? "En Route" : "Closures"} value={isLive ? carCounts.en_route : closures.length} icon={isLive ? ArrowRight : Construction} color="text-yellow-600" />
            <StatCard label={isLive ? "Done" : "Pending"} value={isLive ? carCounts.done : postCounts.pending} icon={isLive ? CheckCircle : Clock} color="text-blue-600" />
          </div>

          {/* Recent activity in live mode */}
          {isLive && audit.length > 0 && (
            <Card className="mt-4">
              <CardHeader><CardTitle className="text-sm">Recent Activity</CardTitle></CardHeader>
              <CardContent>
                <ScrollArea className="h-64">
                  <div className="space-y-2">
                    {audit.slice(0, 20).map((entry, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm border-b pb-1">
                        <span className="text-muted-foreground whitespace-nowrap">{fmtTime(entry.created_at || entry.time)}</span>
                        <Badge variant="outline" className="text-xs">{entry.action}</Badge>
                        <span>{entry.details || entry.target || ""}</span>
                        <span className="text-muted-foreground ml-auto">{entry.user_name || entry.user || ""}</span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── Posts ──────────────────────────────────────────────────── */}
        <TabsContent value="posts">
          <Tabs value={postsSubTab} onValueChange={setPostsSubTab}>
            <TabsList className="mb-3">
              <TabsTrigger value="list">Posts</TabsTrigger>
              <TabsTrigger value="cars">Cars</TabsTrigger>
              <TabsTrigger value="map"><MapIcon className="h-4 w-4 mr-1 inline" />Map</TabsTrigger>
              <TabsTrigger value="updates">Updates{updates.length > 0 ? ` (${updates.length})` : ""}</TabsTrigger>
            </TabsList>

            {isLive && routes.length > 0 && (
              <div className="flex flex-wrap items-center gap-3 mb-3 p-2 bg-muted/30 rounded-lg border">
                <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Lead / Tail</span>
                {routes.map(route => (
                  <div key={route.route_id} className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-full border" style={{ backgroundColor: route.color || "#2563EB" }}></span>
                    <span className="text-xs font-medium mr-0.5">{route.name}</span>
                    <Button size="sm" variant={route.lead_position ? "default" : "outline"} className="h-6 text-[10px] px-2" onClick={() => { setLeadTailTarget({ routeId: route.route_id, type: "lead", color: route.color, existingPosition: route.lead_position }); setShowLeadTailDialog(true); }}>
                      L
                    </Button>
                    <Button size="sm" variant={route.tail_position ? "default" : "outline"} className="h-6 text-[10px] px-2" onClick={() => { setLeadTailTarget({ routeId: route.route_id, type: "tail", color: route.color, existingPosition: route.tail_position }); setShowLeadTailDialog(true); }}>
                      T
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <TabsContent value="list">
          <div className="space-y-4">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2">
              {!isLive && activeEvent.status !== "completed" && (
                <Button size="sm" onClick={() => { setEditingPost({ post_number: "", location: "", specific: "", notes: "", position: "", assigned_car: "", unit: "", status: "pending", lat: "", lng: "" }); setShowEditPost(true); }}>
                  <Plus className="h-4 w-4 mr-1" /> Add Post
                </Button>
              )}
              <SearchableFilter
                value={postStatusFilter}
                onChange={setPostStatusFilter}
                placeholder="Status"
                options={[
                  { value: "all", label: "All Statuses" },
                  { value: "pending", label: "Pending" },
                  { value: "enroute", label: "En Route" },
                  { value: "covered", label: "Covered" },
                  { value: "open", label: "Open" },
                  { value: "cleared", label: "Cleared" },
                ]}
              />
              <SearchableFilter
                value={postCarFilter}
                onChange={setPostCarFilter}
                placeholder="Car"
                options={[
                  { value: "all", label: "All Cars" },
                  ...cars.map((c) => ({
                    value: String(c.car_number),
                    label: c.car_number || `Car ${c.car_number}`,
                  })),
                ]}
              />
              <SearchableFilter
                value={postRouteFilter}
                onChange={setPostRouteFilter}
                placeholder="Route"
                options={[
                  { value: "all", label: "All Routes" },
                  { value: "__none__", label: "No route" },
                  ...routes.map((r) => ({
                    value: r.route_id,
                    label: r.name,
                  })),
                ]}
              />
              <Input
                value={postSearchNumber}
                onChange={(e) => setPostSearchNumber(e.target.value)}
                placeholder="Post #"
                className="h-8 w-24 text-xs"
              />
              <Input
                value={postSearchUnit}
                onChange={(e) => setPostSearchUnit(e.target.value)}
                placeholder="Unit #"
                className="h-8 w-24 text-xs"
              />
              <Input
                value={postSearchAny}
                onChange={(e) => setPostSearchAny(e.target.value)}
                placeholder="Search any text…"
                className="h-8 w-44 text-xs"
              />
              <Select value={postSortMode} onValueChange={setPostSortMode}>
                <SelectTrigger className="h-8 w-[150px] text-xs">
                  <SelectValue placeholder="Sort by…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="number">Sort: #</SelectItem>
                  <SelectItem value="custom">Sort: Custom</SelectItem>
                  <SelectItem value="ring">Sort: Ring</SelectItem>
                  <SelectItem value="priority">Sort: Priority</SelectItem>
                  <SelectItem value="car">Sort: Car</SelectItem>
                </SelectContent>
              </Select>
              {postSortMode === "custom" && (
                <Button size="sm" variant="outline" onClick={() => setShowCustomOrderDialog(true)}>
                  <Edit className="h-3.5 w-3.5 mr-1" /> Edit Order
                </Button>
              )}
              <Button size="sm" variant="outline" className="ml-auto" onClick={() => setShowAddUpdate(true)}>
                <Plus className="h-4 w-4 mr-1" /> Add Update
              </Button>
            </div>

            {/* Table — scrolls inside the page so the toolbar above stays
                fixed when the post list is tall. Sticky thead keeps the
                column headers visible during vertical scroll. */}
            <div className="overflow-auto border rounded-lg" style={{ maxHeight: "calc(100vh - 320px)" }}>
              <table className="w-full text-sm">
                <thead className="bg-muted sticky top-0 z-10">
                  <tr>
                    <th className="p-2 text-left">Post #</th>
                    <th className="p-2 text-left">Location</th>
                    <th className="p-2 text-left hidden md:table-cell">Specific</th>
                    <th className="p-2 text-left hidden md:table-cell">Position</th>
                    <th className="p-2 text-left hidden lg:table-cell">Notes</th>
                    <th className="p-2 text-left">Car</th>
                    <th className="p-2 text-left hidden md:table-cell">Unit</th>
                    <th className="p-2 text-left">Status</th>
                    {isLive && <th className="p-2 text-left hidden md:table-cell">Covered At</th>}
                    <th className="p-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPosts.map((post) => {
                    const rowClass = isLive ? (statusColor[post.status] || "") : "";
                    return (
                      <tr key={post.post_number} className={`border-b cursor-pointer ${isLive ? rowClass : "hover:bg-muted/50"}`}
                          onClick={(e) => {
                            // Defensive: ignore clicks that bubbled from any
                            // interactive child (button, select, link, etc.).
                            // On some browsers / pointer setups, the inner
                            // stopPropagation isn't enough.
                            if (e.target.closest("button, select, [role='button'], [role='combobox'], [data-no-row-click], a, input")) return;
                            setViewingPost(post);
                          }}>
                        <td className="p-2 font-mono">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {postRouteIds(post).map((rid) => {
                              const r = routes.find(rt => rt.route_id === rid);
                              if (!r) return null;
                              return (
                                <span
                                  key={rid}
                                  className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                                  style={{ backgroundColor: r.color || "#2563EB" }}
                                  title={`Route: ${r.name}`}
                                />
                              );
                            })}
                            <span>{post.post_number}</span>
                            {post.priority && <PriorityBadge priority={post.priority} />}
                            {post.ring_enabled && post.ring_at && (
                              <span className="inline-flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" />
                                {new Date(post.ring_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-2">{post.location}</td>
                        <td className="p-2 hidden md:table-cell">{post.specific}</td>
                        <td className="p-2 hidden md:table-cell">{post.position || "-"}</td>
                        <td className="p-2 hidden lg:table-cell max-w-[240px] whitespace-pre-wrap break-words align-top">{post.notes}</td>
                        <td className="p-2" onClick={(e) => e.stopPropagation()}>
                          {isLive ? (
                            <Select value={post.assigned_car || "none"} onValueChange={(v) => {
                              const newCar = v === "none" ? "" : v;
                              if (newCar !== (post.assigned_car || "")) reassignPostCar(post.post_number, newCar);
                            }}>
                              <SelectTrigger className="h-7 w-[120px] text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                {cars.map((c) => (
                                  <SelectItem key={c.car_number} value={String(c.car_number)}>
                                    {c.car_number}{c.unit_number ? ` (${c.unit_number})` : ""}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span>{post.assigned_car_number || post.assigned_car || "-"}</span>
                          )}
                        </td>
                        <td className="p-2 hidden md:table-cell">{post.assigned_unit || post.unit || "-"}</td>
                        <td className="p-2">
                          <Badge className={statusColor[post.status] || ""}>{post.status || "pending"}</Badge>
                        </td>
                        {isLive && <td className="p-2 hidden md:table-cell text-xs">{fmtTime(post.covered_at)}</td>}
                        <td className="p-2" onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-1">
                            {isLive ? (
                              <Select value={post.status || "pending"} onValueChange={(v) => {
                                if (v === post.status) return;
                                const pn = post.post_number;
                                if (v === "pending") pendingPost(pn);
                                else if (v === "enroute") enroutePost(pn);
                                else if (v === "covered") checkInPost(pn);
                                else if (v === "open") openPost(pn);
                                else if (v === "cleared") clearPost(pn);
                              }}>
                                <SelectTrigger className="h-7 w-[110px] text-xs" onClick={(e) => e.stopPropagation()}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="enroute">En Route</SelectItem>
                                  <SelectItem value="covered">Covered</SelectItem>
                                  <SelectItem value="open">Open</SelectItem>
                                  <SelectItem value="cleared">Cleared</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : activeEvent.status !== "completed" ? (
                              <>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); setEditingPost({ ...post, _isEdit: true }); setShowEditPost(true); }}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0"
                                  title="Copy post (creates a new one with the same data)"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Strip identity + runtime fields so the
                                    // dialog opens as a fresh "Add Post"
                                    // pre-filled from this post. User picks
                                    // a new Post # before saving.
                                    const {
                                      post_number: _pn, status: _st, history: _h,
                                      covered_at: _ca, covered_by: _cb,
                                      enroute_at: _ea, opened_at: _oa, cleared_at: _cl,
                                      ring_sent_at: _rsa, ring_acknowledged_at: _raa, ring_acknowledged_by: _rab,
                                      ...rest
                                    } = post;
                                    setEditingPost({ ...rest, post_number: "" });
                                    setShowEditPost(true);
                                  }}
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={(e) => { e.stopPropagation(); deletePost(post.post_number); }}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredPosts.length === 0 && (
                    <tr><td colSpan={isLive ? 10 : 9} className="p-4 text-center text-muted-foreground">No posts found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
            </TabsContent>

            {/* ── Cars (sub-tab) ────────────────────────────────────── */}
            <TabsContent value="cars">
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              {!isLive && activeEvent.status !== "completed" && (
                <Button size="sm" onClick={() => { setEditingCar({ car_number: "", unit_number: "", role: "", post_sequence: [], status: "standby" }); setShowEditCar(true); }}>
                  <Plus className="h-4 w-4 mr-1" /> Add Car
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowUnitRoster(true)}
              >
                <Users className="h-3.5 w-3.5 mr-1" /> Unit Roster
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setExportCarsText(buildAllCarsExport())}
              >
                <Save className="h-3.5 w-3.5 mr-1" /> Export All
              </Button>
              <Input
                value={carSearch}
                onChange={(e) => setCarSearch(e.target.value)}
                placeholder="Search car #, unit, role…"
                className="h-8 w-56 text-xs"
              />
              <div className="flex items-center gap-1.5 ml-auto">
                <span className="text-xs text-muted-foreground">Sort by:</span>
                <Select value={carSort} onValueChange={setCarSort}>
                  <SelectTrigger className="h-8 w-[140px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="car">Car #</SelectItem>
                    <SelectItem value="unit">Unit #</SelectItem>
                    <SelectItem value="post">Current Post #</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortedCars.map((car) => {
                const seq = car.post_sequence || [];
                // Cleared posts are skipped from the car's view — find current
                // and next from the cleared-filtered sequence so a cleared
                // post never surfaces as Current or Next. The full sequence
                // (with its cleared badges) is still rendered below.
                const visibleSeq = seq.filter((pn) => {
                  const p = posts.find((pp) => String(pp.post_number) === String(pn));
                  return p?.status !== "cleared";
                });
                const currentIdxFull = car.current_post != null
                  ? seq.findIndex((s) => String(s) === String(car.current_post))
                  : -1;
                const currentVisIdx = car.current_post != null
                  ? visibleSeq.findIndex((s) => String(s) === String(car.current_post))
                  : -1;
                // If current_post isn't in the visible seq (no current set
                // yet, or it points at a cleared post), Current falls back
                // to visibleSeq[0] and Next must follow as visibleSeq[1] —
                // not stay null because currentVisIdx is -1.
                const effectiveCurIdx = currentVisIdx >= 0 ? currentVisIdx : (visibleSeq.length > 0 ? 0 : -1);
                const currentPostNum = effectiveCurIdx >= 0 ? visibleSeq[effectiveCurIdx] : null;
                const nextPostNum = effectiveCurIdx >= 0 && effectiveCurIdx < visibleSeq.length - 1
                  ? visibleSeq[effectiveCurIdx + 1]
                  : null;
                const currentPost = currentPostNum ? posts.find((p) => String(p.post_number) === String(currentPostNum)) : null;
                const nextPost = nextPostNum ? posts.find((p) => String(p.post_number) === String(nextPostNum)) : null;
                // Completed counter: posts at-or-before the current sequence
                // position (full seq, cleared posts count toward "done").
                const currentIdx = currentIdxFull;
                const completedCount = currentIdxFull >= 0 ? currentIdxFull + 1 : 0;

                // Derive a *live* car status from the actual posts in the
                // sequence so the top-right badge always matches reality.
                // The backend stores car.status which can drift (e.g. it
                // marked the car "done" but a post just flipped to "open").
                // Priority: open > en_route > posted (any covered, none later) >
                // done (all cleared) > standby (default / all pending).
                const seqPosts = seq
                  .map((pn) => posts.find((p) => String(p.post_number) === String(pn)))
                  .filter(Boolean);
                let derivedStatus = car.status || "standby";
                let derivedLabel = derivedStatus;
                if (seqPosts.length > 0) {
                  const has = (s) => seqPosts.some((p) => p.status === s);
                  const all = (s) => seqPosts.every((p) => p.status === s);
                  // "open" on a post is a per-post state and doesn't change
                  // what the car itself is doing — skip it from the car
                  // badge so the badge always reflects the car's own state.
                  if (has("enroute")) { derivedStatus = "en_route"; derivedLabel = "en route"; }
                  else if (all("cleared")) { derivedStatus = "done"; derivedLabel = "all cleared"; }
                  else if (has("covered")) { derivedStatus = "posted"; derivedLabel = "posted"; }
                  else if (all("pending")) { derivedStatus = "standby"; derivedLabel = "standby"; }
                  else { derivedStatus = "standby"; derivedLabel = "standby"; }
                }
                // Map derived status to existing color palettes:
                //   en_route → amber (statusColor.enroute),
                //   posted/done/standby → reuse carStatusColor.
                const derivedBadgeClass =
                  derivedStatus === "en_route" ? statusColor.enroute :
                  carStatusColor[derivedStatus] || carStatusColor.standby;

                return (
                <Card key={car.car_number}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">Car {car.car_number}</CardTitle>
                        {car.unit_number && <p className="text-sm text-muted-foreground">Unit {car.unit_number}</p>}
                      </div>
                      <div className="text-right flex flex-col items-end gap-1">
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            title="Export this car (sequence as text)"
                            onClick={(e) => { e.stopPropagation(); setExportCarsText(buildCarExport(car)); }}
                          >
                            <Save className="h-3.5 w-3.5" />
                          </Button>
                          <Badge className={derivedBadgeClass}>{derivedLabel}</Badge>
                        </div>
                        {car.role && <p className="text-xs text-muted-foreground">{car.role}</p>}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Current Post — tap anywhere to open the post-info dialog. */}
                    {currentPost ? (
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 cursor-pointer hover:bg-green-500/15"
                           onClick={() => setViewingPost(currentPost)}>
                        <p className="text-[10px] text-green-500 uppercase font-semibold mb-1">Current Post</p>
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-bold text-base">Post #{currentPost.post_number}</p>
                            <p className="text-sm">{currentPost.location}</p>
                            {currentPost.specific && <p className="text-xs text-muted-foreground">{currentPost.specific}</p>}
                          </div>
                          {currentPost.position && <Badge variant="outline" className="text-xs shrink-0">{currentPost.position}</Badge>}
                        </div>
                        {currentPost.notes && <p className="text-xs text-muted-foreground mt-1">{currentPost.notes}</p>}
                        {postRouteIds(currentPost).length > 0 && (
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {postRouteIds(currentPost).map((rid) => {
                              const r = routes.find(rt => rt.route_id === rid);
                              if (!r) return null;
                              return (
                                <span key={rid} className="inline-flex items-center gap-1">
                                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: r.color || "#2563EB" }}></span>
                                  <span className="text-xs font-medium" style={{ color: r.color || "#2563EB" }}>{r.name}</span>
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : seq.length > 0 && visibleSeq.length === 0 ? (
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-center">
                        <p className="text-[10px] text-blue-400 uppercase font-semibold mb-1">Done</p>
                        <p className="text-sm font-medium text-blue-200">
                          All {seq.length} post{seq.length === 1 ? "" : "s"} cleared
                        </p>
                      </div>
                    ) : seq.length > 0 ? (
                      (() => {
                        const fp = posts.find((p) => String(p.post_number) === String(seq[0]));
                        const onClick = fp ? () => setViewingPost(fp) : undefined;
                        return (
                          <div className={`bg-muted/50 border rounded-lg p-3 ${fp ? "cursor-pointer hover:bg-muted/70" : ""}`}
                               onClick={onClick}>
                            <p className="text-[10px] text-muted-foreground uppercase font-semibold mb-1">First Post</p>
                            {fp ? (
                              <div>
                                <p className="font-bold">Post #{fp.post_number}</p>
                                <p className="text-sm">{fp.location}</p>
                                {fp.specific && <p className="text-xs text-muted-foreground">{fp.specific}</p>}
                              </div>
                            ) : <p className="text-sm">Post #{seq[0]}</p>}
                          </div>
                        );
                      })()
                    ) : (
                      <p className="text-xs text-muted-foreground italic">No posts assigned</p>
                    )}

                    {/* Next Post — tap anywhere to open the post-info dialog. */}
                    {nextPost && (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 cursor-pointer hover:bg-amber-500/15"
                           onClick={() => setViewingPost(nextPost)}>
                        <p className="text-[10px] text-amber-500 uppercase font-semibold mb-1">Next Post</p>
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-bold text-base">Post #{nextPost.post_number}</p>
                            <p className="text-sm">{nextPost.location}</p>
                            {nextPost.specific && <p className="text-xs text-muted-foreground">{nextPost.specific}</p>}
                          </div>
                          {nextPost.position && <Badge variant="outline" className="text-xs shrink-0">{nextPost.position}</Badge>}
                        </div>
                        {nextPost.notes && <p className="text-xs text-muted-foreground mt-1">{nextPost.notes}</p>}
                        {postRouteIds(nextPost).length > 0 && (
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {postRouteIds(nextPost).map((rid) => {
                              const r = routes.find(rt => rt.route_id === rid);
                              if (!r) return null;
                              return (
                                <span key={rid} className="inline-flex items-center gap-1">
                                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: r.color || "#2563EB" }}></span>
                                  <span className="text-xs font-medium" style={{ color: r.color || "#2563EB" }}>{r.name}</span>
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Sequence progress — color each badge by the post's
                        actual status so dispatchers see at a glance which
                        posts are still pending vs en route / covered. */}
                    {seq.length > 0 && (() => {
                      const statusCounts = { pending: 0, enroute: 0, covered: 0, open: 0, cleared: 0 };
                      const seqWithPosts = seq.map((pn) => {
                        const p = posts.find((pp) => String(pp.post_number) === String(pn));
                        const s = (p?.status || "pending");
                        if (s in statusCounts) statusCounts[s] += 1;
                        return { pn, post: p, status: s };
                      });
                      const summaryBits = [];
                      if (statusCounts.pending) summaryBits.push(`${statusCounts.pending} pending`);
                      if (statusCounts.enroute) summaryBits.push(`${statusCounts.enroute} en route`);
                      if (statusCounts.covered) summaryBits.push(`${statusCounts.covered} covered`);
                      if (statusCounts.open) summaryBits.push(`${statusCounts.open} open`);
                      if (statusCounts.cleared) summaryBits.push(`${statusCounts.cleared} cleared`);
                      return (
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase font-semibold mb-1">
                            Sequence ({completedCount}/{seq.length})
                            {summaryBits.length > 0 && (
                              <span className="text-muted-foreground/80 normal-case font-normal lowercase ml-1">
                                — {summaryBits.join(", ")}
                              </span>
                            )}
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {seqWithPosts.map(({ pn, post: postInfo, status }, i) => {
                              const isCurrent = String(pn) === String(car.current_post);
                              const colorClass = statusColor[status] || statusColor.pending;
                              return (
                                <Badge
                                  key={pn}
                                  className={`text-xs ${colorClass} ${isCurrent ? "ring-2 ring-yellow-400" : ""}`}
                                  title={postInfo
                                    ? `Post #${pn} — ${status}\n${postInfo.location || ""}${postInfo.specific ? ` (${postInfo.specific})` : ""}`
                                    : `Post #${pn} — ${status}`}
                                >
                                  {pn}
                                </Badge>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Actions */}
                    <div className="space-y-2 pt-2 border-t">
                      {isLive && currentPost && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground whitespace-nowrap">Post #{currentPost.post_number}:</span>
                          <Select value={currentPost.status || "pending"} onValueChange={(v) => {
                            if (v === currentPost.status) return;
                            const pn = currentPost.post_number;
                            if (v === "pending") pendingPost(pn);
                            else if (v === "enroute") enroutePost(pn);
                            else if (v === "covered") checkInPost(pn);
                            else if (v === "open") openPost(pn);
                            else if (v === "cleared") clearPost(pn);
                          }}>
                            <SelectTrigger className="h-7 w-[110px] text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="enroute">En Route</SelectItem>
                              <SelectItem value="covered">Covered</SelectItem>
                              <SelectItem value="open">Open</SelectItem>
                              <SelectItem value="cleared">Cleared</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="flex gap-1 flex-wrap">
                        {isLive && car.status !== "done" && nextPost && (() => {
                          // Block "Next →" until the current post has been
                          // marked open or cleared. Keeps a car from skipping
                          // ahead while it's still actively at a post.
                          const canAdvance = !currentPost
                            || currentPost.status === "open"
                            || currentPost.status === "cleared";
                          const blockedReason = canAdvance
                            ? null
                            : `Mark Post #${currentPost.post_number} as Open or Cleared first.`;
                          return (
                            <div title={blockedReason || ""}>
                              <Select value="_next" disabled={!canAdvance} onValueChange={(v) => {
                                if (v === "_next") return;
                                moveCarNext(car.car_number).then(() => {
                                  const pn = nextPost.post_number;
                                  if (v === "enroute") enroutePost(pn);
                                  else if (v === "covered") checkInPost(pn);
                                  else if (v === "open") openPost(pn);
                                });
                              }}>
                                <SelectTrigger className="h-8 w-[160px] text-xs">
                                  <span className="flex items-center gap-1"><ChevronRight className="h-3 w-3" /> Next → Post #{nextPost.post_number}</span>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_next" disabled>Select action...</SelectItem>
                                  <SelectItem value="enroute">Mark En Route</SelectItem>
                                  <SelectItem value="covered">Mark Covered</SelectItem>
                                  <SelectItem value="open">Mark Open</SelectItem>
                                </SelectContent>
                              </Select>
                              {!canAdvance && (
                                <p className="text-[10px] text-muted-foreground mt-1">
                                  Mark Post #{currentPost.post_number} Open or Cleared to advance.
                                </p>
                              )}
                            </div>
                          );
                        })()}
                        {isLive && car.status !== "done" && !nextPost && seq.length > 0 && (
                          <Button size="sm" variant="outline" className="text-xs" onClick={() => {
                            if (window.confirm("Mark car as done? No more posts in sequence.")) {
                              // Just move to trigger done status
                              moveCarNext(car.car_number);
                            }
                          }}>
                            <CheckCircle className="h-4 w-4 mr-1" /> Mark Done
                          </Button>
                        )}
                        {!isLive && activeEvent.status !== "completed" && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => { setEditingCar({ ...car, _isEdit: true }); setShowEditCar(true); }}>
                              <Edit className="h-4 w-4 mr-1" /> Edit
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteCar(car.car_number)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    {/* "Show all posts" lives at the bottom of the card so the
                        Current/Next/Sequence/Actions block stays compact. */}
                    {seqPosts.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-2"
                        onClick={() => setAllPostsCarNumber(car.car_number)}
                      >
                        Show all {seqPosts.length} posts
                      </Button>
                    )}
                  </CardContent>
                </Card>
                );
              })}
              {cars.length === 0 && (
                <p className="text-muted-foreground text-sm col-span-full">No cars configured.</p>
              )}
            </div>
          </div>
            </TabsContent>

            {/* ── Map (sub-tab) with pin mode switcher ──────────────── */}
            <TabsContent value="map">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-muted-foreground">Pins:</span>
                {[{ value: "post", label: "Post #" }, { value: "car", label: "Car #" }, { value: "unit", label: "Unit #" }].map((m) => (
                  <Button
                    key={m.value}
                    size="sm"
                    variant={mapPinMode === m.value ? "default" : "outline"}
                    className="h-7 text-xs px-2"
                    onClick={() => setMapPinMode(mapPinMode === m.value ? null : m.value)}
                  >
                    {m.label}
                  </Button>
                ))}
                {!mapPinMode && (
                  <span className="text-[10px] text-muted-foreground italic ml-1">Posts hidden — pick a label</span>
                )}
              </div>
              <PostMapTab
                posts={posts}
                hidePins={!mapPinMode}
                cars={cars}
                isLive={isLive}
                labelMode={mapPinMode}
                routes={routes}
                visibleRouteIds={visibleRouteIds}
                onToggleRoute={(routeId) => setVisibleRouteIds((prev) => { const s = new Set(prev); if (s.has(routeId)) s.delete(routeId); else s.add(routeId); return s; })}
                onCheckIn={checkInPost}
                onOpen={openPost}
                onClear={clearPost}
                onEnroute={enroutePost}
                onSetPending={pendingPost}
                onReassignCar={reassignPostCar}
                onMovePin={async (postNumber, lat, lng) => {
                  try {
                    await ax.put(`${API}/event-ops/events/${activeEvent.event_id}/posts/${postNumber}`, { lat, lng });
                    fetchEventData(activeEvent.event_id);
                  } catch {}
                }}
              />
            </TabsContent>

            {/* ── Updates (manual notes) ─────────────────────────────── */}
            <TabsContent value="updates">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-semibold">Event Updates</h3>
                  <p className="text-xs text-muted-foreground">Notes can be tagged to a post, car, or unit. Sorted newest first.</p>
                </div>
                <Button size="sm" onClick={() => setShowAddUpdate(true)}>
                  <Plus className="h-4 w-4 mr-1" /> Add Update
                </Button>
              </div>
              {updates.length === 0 ? (
                <p className="text-sm text-muted-foreground italic border rounded p-4 text-center">No updates yet.</p>
              ) : (
                <div className="space-y-2">
                  {updates.map((u) => {
                    const tagLabel = (() => {
                      if (u.target_type === "post") return `Post #${u.target_id}`;
                      if (u.target_type === "car") {
                        const carObj = cars.find(c => String(c.car_number) === String(u.target_id));
                        return `Car ${u.target_id}${carObj?.unit_number ? ` (${carObj.unit_number})` : ""}`;
                      }
                      if (u.target_type === "unit") return `Unit ${u.target_id}`;
                      return "General";
                    })();
                    const tagColor = u.target_type === "post"
                      ? "bg-blue-600 text-white"
                      : u.target_type === "car"
                      ? "bg-purple-600 text-white"
                      : u.target_type === "unit"
                      ? "bg-emerald-600 text-white"
                      : "bg-slate-500 text-white";
                    return (
                      <div key={u.update_id} className="border rounded-lg p-3 bg-muted/30">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={`text-[10px] ${tagColor}`}>{tagLabel}</Badge>
                            <span className="text-xs text-muted-foreground">
                              {u.timestamp ? new Date(u.timestamp).toLocaleString() : ""}
                            </span>
                            {u.by_user_name && (
                              <span className="text-xs text-muted-foreground">— {u.by_user_name}</span>
                            )}
                          </div>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500"
                                  onClick={() => deleteUpdate(u.update_id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <p className="text-sm whitespace-pre-wrap break-words">{u.text}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* ── Schools ───────────────────────────────────────────────── */}
        <TabsContent value="schools">
          <div className="space-y-4">
            {!isLive && activeEvent.status !== "completed" && (
              <Button size="sm" onClick={() => { setEditingSchool({ name: "", location: "", coordinator: "", contact: "", children_count: "", divisions: "", street_closures: "", notes: "" }); setShowEditSchool(true); }}>
                <Plus className="h-4 w-4 mr-1" /> Add School
              </Button>
            )}
            {schools.map((school) => (
              <Card key={school.school_id}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <School className="h-4 w-4" /> {school.name}
                    </CardTitle>
                    <div className="flex gap-1">
                      {!isLive && activeEvent.status !== "completed" && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => { setEditingSchool({ ...school, _isEdit: true }); setShowEditSchool(true); }}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteSchool(school.school_id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    {school.location && <div><span className="text-muted-foreground">Location:</span> {school.location}</div>}
                    {(() => { const r = routes.find(rt => rt.route_id === school.route_id); return r ? (
                      <div className="flex items-center gap-1.5">
                        <span className="text-muted-foreground">Route:</span>
                        <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: r.color || "#2563EB" }}></span>
                        <span className="font-medium" style={{ color: r.color || "#2563EB" }}>{r.name}</span>
                      </div>
                    ) : null; })()}
                    {school.coordinator && <div><span className="text-muted-foreground">Coordinator:</span> {school.coordinator}</div>}
                    {school.contact && <div><span className="text-muted-foreground">Contact:</span> {school.contact}</div>}
                    {school.children_count && <div><span className="text-muted-foreground">Children:</span> {school.children_count}</div>}
                    {school.divisions && <div><span className="text-muted-foreground">Divisions:</span> {school.divisions}</div>}
                    {school.street_closures && <div><span className="text-muted-foreground">Street Closures:</span> {school.street_closures}</div>}
                    {school.notes && <div className="col-span-full"><span className="text-muted-foreground">Notes:</span> {school.notes}</div>}
                  </div>
                </CardContent>
              </Card>
            ))}
            {schools.length === 0 && <p className="text-muted-foreground text-sm">No schools added.</p>}
          </div>
        </TabsContent>

        {/* ── Closures ──────────────────────────────────────────────── */}
        <TabsContent value="closures">
          <div className="space-y-4">
            {!isLive && activeEvent.status !== "completed" && (
              <Button size="sm" onClick={() => { setEditingClosure({ street: "", segment: "", time_from: "", time_to: "", notes: "" }); setShowEditClosure(true); }}>
                <Plus className="h-4 w-4 mr-1" /> Add Closure
              </Button>
            )}
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="p-2 text-left">Street</th>
                    <th className="p-2 text-left">Segment</th>
                    <th className="p-2 text-left">From</th>
                    <th className="p-2 text-left">To</th>
                    <th className="p-2 text-left">Notes</th>
                    {!isLive && activeEvent.status !== "completed" && <th className="p-2 text-left">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {closures.map((cl) => (
                    <tr key={cl.closure_id || cl.street} className="border-b hover:bg-muted/50">
                      <td className="p-2">{cl.street}</td>
                      <td className="p-2">{cl.segment}</td>
                      <td className="p-2">{cl.time_from}</td>
                      <td className="p-2">{cl.time_to}</td>
                      <td className="p-2">{cl.notes}</td>
                      {!isLive && activeEvent.status !== "completed" && (
                        <td className="p-2">
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditingClosure({ ...cl, _isEdit: true }); setShowEditClosure(true); }}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => deleteClosure(cl.closure_id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                  {closures.length === 0 && (
                    <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">No street closures</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* ── Routes ─────────────────────────────────────────────────── */}
        <TabsContent value="routes">
          <div className="space-y-4">
            {!isLive && activeEvent.status !== "completed" && (
              <Button size="sm" onClick={() => { setEditingRoute({ name: "", school: "", color: "#2563EB", begin_time: "", waypoints: [] }); setShowEditRoute(true); }}>
                <Plus className="h-4 w-4 mr-1" /> Add Route
              </Button>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {routes.map((route) => (
                <Card key={route.route_id} className="cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => setViewingRoute(route)}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <span className="inline-block w-4 h-4 rounded-full border" style={{ backgroundColor: route.color || "#2563EB" }}></span>
                        {route.name}
                      </CardTitle>
                      {!isLive && activeEvent.status !== "completed" && (
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setEditingRoute({ ...route, _isEdit: true }); setShowEditRoute(true); }}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={(e) => { e.stopPropagation(); deleteRoute(route.route_id); }}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    {route.begin_time && <div><span className="text-muted-foreground">Begin:</span> {route.begin_time}</div>}
                    <div><span className="text-muted-foreground">Waypoints:</span> {(route.waypoints || []).length}</div>
                    {isLive && (
                      <div className="border-t pt-2 mt-2 space-y-2">
                        {route.lead_position && (
                          <div className="text-xs"><span className="text-muted-foreground">Lead:</span> {route.lead_position.lat.toFixed(5)}, {route.lead_position.lng.toFixed(5)}</div>
                        )}
                        {route.tail_position && (
                          <div className="text-xs"><span className="text-muted-foreground">Tail:</span> {route.tail_position.lat.toFixed(5)}, {route.tail_position.lng.toFixed(5)}</div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              {routes.length === 0 && (
                <p className="text-muted-foreground text-sm col-span-full">No routes configured.</p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ── Audit ─────────────────────────────────────────────────── */}
        <TabsContent value="audit">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Select value={auditActionFilter} onValueChange={setAuditActionFilter}>
                <SelectTrigger className="w-[180px]"><SelectValue placeholder="Filter action" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="check_in">Check In</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="clear">Clear</SelectItem>
                  <SelectItem value="car_move">Car Move</SelectItem>
                  <SelectItem value="go_live">Go Live</SelectItem>
                  <SelectItem value="complete">Complete</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={() => fetchAudit(activeEvent.event_id)}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="p-2 text-left">Time</th>
                    <th className="p-2 text-left">User</th>
                    <th className="p-2 text-left">Action</th>
                    <th className="p-2 text-left">Target</th>
                    <th className="p-2 text-left">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAudit.map((entry, i) => (
                    <tr key={i} className="border-b hover:bg-muted/50 align-top">
                      <td className="p-2 whitespace-nowrap">{fmtTime(entry.timestamp)}</td>
                      <td className="p-2">{entry.user_name || ""}</td>
                      <td className="p-2"><Badge variant="outline">{entry.action}</Badge></td>
                      <td className="p-2">
                        {entry.target_type ? `${entry.target_type} ${entry.target_id || ""}` : ""}
                      </td>
                      <td className="p-2 text-xs text-muted-foreground">
                        {formatAuditDetails(entry)}
                      </td>
                    </tr>
                  ))}
                  {filteredAudit.length === 0 && (
                    <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">No audit entries</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* ── Setup (plan mode) ─────────────────────────────────────── */}
        <TabsContent value="setup">
          <div className="space-y-4">
            <Card>
              <CardHeader><CardTitle>Event Details</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div><span className="text-muted-foreground">Name:</span> {activeEvent.name}</div>
                <div><span className="text-muted-foreground">Date:</span> {activeEvent.date}</div>
                <div><span className="text-muted-foreground">Status:</span> {activeEvent.status || "plan"}</div>
                <div><span className="text-muted-foreground">Posts:</span> {posts.length}</div>
                <div><span className="text-muted-foreground">Cars:</span> {cars.length}</div>
                <div><span className="text-muted-foreground">Schools:</span> {schools.length}</div>
                <div><span className="text-muted-foreground">Closures:</span> {closures.length}</div>
              </CardContent>
            </Card>
            {canDispatch && isAdmin && (
              <Card>
                <CardHeader><CardTitle>Ring Alerts → WhatsApp</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <p className="text-xs text-muted-foreground">
                    When a post's ringer fires, blast a WhatsApp message to these groups
                    (in addition to the dispatcher push notifications).
                  </p>
                  {botGroups.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">
                      No bot groups available — configure in Settings → WhatsApp.
                    </p>
                  ) : (
                    <div className="space-y-1 max-h-48 overflow-y-auto border rounded p-2">
                      {botGroups.map((g) => {
                        const jid = g.jid || g.id || g;
                        const name = g.name || g.subject || jid;
                        const checked = (activeEvent.ring_whatsapp_group_jids || []).includes(jid);
                        return (
                          <label key={jid} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={async (e) => {
                                const cur = activeEvent.ring_whatsapp_group_jids || [];
                                const next = e.target.checked ? [...cur, jid] : cur.filter(j => j !== jid);
                                try {
                                  const { data } = await ax.put(
                                    `${API}/event-ops/events/${activeEvent.event_id}`,
                                    { ring_whatsapp_group_jids: next }
                                  );
                                  setActiveEvent(data);
                                } catch {
                                  toast.error("Failed to update ring groups");
                                }
                              }}
                              className="w-4 h-4"
                            />
                            <span>{name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            {canDispatch && (
              <Card>
                <CardHeader><CardTitle>Dispatch Actions</CardTitle></CardHeader>
                <CardContent className="flex gap-2">
                  <Button variant="destructive" onClick={() => setShowGoLiveConfirm(true)}>
                    <Play className="h-4 w-4 mr-1" /> Go Live
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* ── Dialogs ───────────────────────────────────────────────────── */}

      {/* Go Live Confirmation */}
      <Dialog open={showGoLiveConfirm} onOpenChange={setShowGoLiveConfirm}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Confirm Go Live</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5" />
              <p className="text-sm">Are you sure you want to set this event to LIVE? Posts will become active and check-in/open/clear actions will be enabled.</p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowGoLiveConfirm(false)}>Cancel</Button>
              <Button variant="destructive" onClick={goLive}>Go Live</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Complete Confirmation */}
      <Dialog open={showCompleteConfirm} onOpenChange={setShowCompleteConfirm}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Complete Event</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm">Mark this event as completed? This will end the live session.</p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowCompleteConfirm(false)}>Cancel</Button>
              <Button onClick={completeEvent}>Complete Event</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Post Edit Dialog */}
      <PostDialog open={showEditPost} onOpenChange={setShowEditPost} post={editingPost} cars={cars} routes={routes} onSave={savePost} />

      {/* Post Detail Dialog */}
      <Dialog open={!!viewingPost} onOpenChange={(open) => { if (!open) setViewingPost(null); }}>
        <DialogContent aria-describedby={undefined} className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Post #{viewingPost?.post_number}</DialogTitle>
          </DialogHeader>
          {viewingPost && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-muted-foreground block text-xs">Location</span><span className="font-medium">{viewingPost.location || "-"}</span></div>
                <div><span className="text-muted-foreground block text-xs">Position</span><span className="font-medium">{viewingPost.position || "-"}</span></div>
              </div>
              {(viewingPost.priority || (viewingPost.ring_enabled && viewingPost.ring_at)) && (
                <div className="flex flex-wrap items-center gap-2">
                  {viewingPost.priority && <PriorityBadge priority={viewingPost.priority} />}
                  {viewingPost.ring_enabled && viewingPost.ring_at && (
                    <span className="inline-flex items-center gap-1 text-xs">
                      <Clock className={`h-3 w-3 ${viewingPost.ring_sent_at ? "text-green-600" : "text-amber-500"}`} />
                      Ring: {new Date(viewingPost.ring_at).toLocaleString()}
                      {viewingPost.ring_sent_at && <span className="text-green-600">(fired)</span>}
                    </span>
                  )}
                </div>
              )}
              {viewingPost.specific && <div><span className="text-muted-foreground block text-xs">Specific</span><span>{viewingPost.specific}</span></div>}
              {viewingPost.notes && <div><span className="text-muted-foreground block text-xs">Notes</span><span>{viewingPost.notes}</span></div>}
              <div className="grid grid-cols-2 gap-3 border-t pt-3">
                <div>
                  <span className="text-muted-foreground block text-xs">Assigned Car</span>
                  {isLive ? (
                    <Select value={viewingPost.assigned_car || "none"} onValueChange={(v) => {
                      const newCar = v === "none" ? "" : v;
                      if (newCar !== (viewingPost.assigned_car || "")) {
                        reassignPostCar(viewingPost.post_number, newCar);
                        setViewingPost(null);
                      }
                    }}>
                      <SelectTrigger className="h-7 text-xs mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {cars.map((c) => (
                          <SelectItem key={c.car_number} value={String(c.car_number)}>{c.car_number}{c.unit_number ? ` (${c.unit_number})` : ""}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="font-medium">{viewingPost.assigned_car || "-"}</span>
                  )}
                </div>
                <div><span className="text-muted-foreground block text-xs">Unit</span><span className="font-medium">{viewingPost.assigned_unit || viewingPost.unit || "-"}</span></div>
              </div>
              <div className="border-t pt-3">
                <span className="text-muted-foreground block text-xs mb-1">Status</span>
                <Badge className={statusColor[viewingPost.status] || ""}>{viewingPost.status || "pending"}</Badge>
                {viewingPost.covered_at && <span className="text-xs text-muted-foreground ml-2">Covered: {fmtTime(viewingPost.covered_at)}</span>}
                {viewingPost.opened_at && <span className="text-xs text-muted-foreground ml-2">Opened: {fmtTime(viewingPost.opened_at)}</span>}
                {viewingPost.cleared_at && <span className="text-xs text-muted-foreground ml-2">Cleared: {fmtTime(viewingPost.cleared_at)}</span>}
                {viewingPost.covered_by && <div className="text-xs text-muted-foreground mt-1">By: {viewingPost.covered_by}</div>}
              </div>
              {postRouteIds(viewingPost).length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-muted-foreground text-xs">Routes:</span>
                  {postRouteIds(viewingPost).map((rid) => {
                    const r = routes.find((rt) => rt.route_id === rid);
                    if (!r) return null;
                    return (
                      <span key={rid} className="inline-flex items-center gap-1">
                        <span
                          className="inline-block w-3 h-3 rounded-full shrink-0"
                          style={{ backgroundColor: r.color || "#2563EB" }}
                        />
                        <span className="text-sm font-medium" style={{ color: r.color || "#2563EB" }}>
                          {r.name}
                        </span>
                      </span>
                    );
                  })}
                </div>
              )}
              {viewingPost.image_url && (
                <div className="border-t pt-3">
                  <span className="text-muted-foreground block text-xs mb-1">Image</span>
                  <img
                    src={getAuthenticatedMediaUrl(`${API.replace('/api', '')}${viewingPost.image_url}/thumb?size=600`)}
                    alt="Post"
                    className="w-full max-h-60 object-contain rounded cursor-pointer"
                    onClick={() => window.open(getAuthenticatedMediaUrl(`${API.replace('/api', '')}${viewingPost.image_url}`), '_blank')}
                  />
                </div>
              )}
              <PostMapPreview post={viewingPost} />
              {isLive && (
                <div className="flex gap-2 border-t pt-3">
                  <Button size="sm" variant="outline" className="text-amber-500" onClick={() => { enroutePost(viewingPost.post_number); setViewingPost(null); }}>En Route</Button>
                  <Button size="sm" variant="outline" className="text-green-600" onClick={() => { checkInPost(viewingPost.post_number); setViewingPost(null); }}>Check In</Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => { openPost(viewingPost.post_number); setViewingPost(null); }}>Open</Button>
                  <Button size="sm" variant="outline" className="text-blue-600" onClick={() => { clearPost(viewingPost.post_number); setViewingPost(null); }}>Clear</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Car Edit Dialog */}
      <CarDialog open={showEditCar} onOpenChange={setShowEditCar} car={editingCar} posts={posts} cars={cars} appUnits={appUnits} onSave={saveCar} />

      {/* School Edit Dialog */}
      <SchoolDialog open={showEditSchool} onOpenChange={setShowEditSchool} school={editingSchool} routes={routes} onSave={saveSchool} />

      {/* Closure Edit Dialog */}
      <ClosureDialog open={showEditClosure} onOpenChange={setShowEditClosure} closure={editingClosure} onSave={saveClosure} />

      {/* Route Edit Dialog */}
      <RouteDialog open={showEditRoute} onOpenChange={setShowEditRoute} route={editingRoute} onSave={saveRoute} />

      {/* Route Detail Dialog */}
      <Dialog open={!!viewingRoute} onOpenChange={(open) => { if (!open) setViewingRoute(null); }}>
        <DialogContent aria-describedby={undefined} className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="inline-block w-4 h-4 rounded-full border" style={{ backgroundColor: viewingRoute?.color || "#2563EB" }}></span>
              {viewingRoute?.name}
            </DialogTitle>
          </DialogHeader>
          {viewingRoute && (() => {
            const routeSchools = schools.filter(s => s.route_id === viewingRoute.route_id);
            const routePosts = posts.filter(p => postRouteIds(p).includes(String(viewingRoute.route_id)));
            return (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><span className="text-muted-foreground block text-xs">Color</span><span className="flex items-center gap-2"><span className="w-4 h-4 rounded border inline-block" style={{ backgroundColor: viewingRoute.color }}></span>{viewingRoute.color}</span></div>
                  <div><span className="text-muted-foreground block text-xs">Begin Time</span><span className="font-medium">{viewingRoute.begin_time || "-"}</span></div>
                </div>
                <div><span className="text-muted-foreground block text-xs">Waypoints</span><span>{(viewingRoute.waypoints || []).length} points</span></div>

                {/* Schools on this route */}
                <div className="border-t pt-3">
                  <span className="text-muted-foreground block text-xs mb-2">Schools on this Route ({routeSchools.length})</span>
                  {routeSchools.length > 0 ? (
                    <div className="space-y-1.5">
                      {routeSchools.map(s => (
                        <div key={s.school_id} className="flex items-center justify-between bg-muted/50 rounded px-2 py-1.5">
                          <div>
                            <p className="font-medium">{s.name}</p>
                            {s.children_count && <p className="text-xs text-muted-foreground">{s.children_count} children</p>}
                          </div>
                          {s.coordinator && <span className="text-xs text-muted-foreground">{s.coordinator}</span>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">No schools assigned to this route</p>
                  )}
                </div>

                {/* Posts on this route */}
                <div className="border-t pt-3">
                  <span className="text-muted-foreground block text-xs mb-2">Posts on this Route ({routePosts.length})</span>
                  {routePosts.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {routePosts.map(p => (
                        <Badge key={p.post_number} variant="outline" className="text-xs">
                          #{p.post_number} {p.location}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">No posts assigned to this route</p>
                  )}
                </div>

                {/* Live positions */}
                {(viewingRoute.lead_position || viewingRoute.tail_position) && (
                  <div className="border-t pt-3">
                    <span className="text-muted-foreground block text-xs mb-1">Live Positions</span>
                    {viewingRoute.lead_position && <p className="text-xs"><span className="font-semibold">Lead:</span> {viewingRoute.lead_position.lat.toFixed(5)}, {viewingRoute.lead_position.lng.toFixed(5)}</p>}
                    {viewingRoute.tail_position && <p className="text-xs"><span className="font-semibold">Tail:</span> {viewingRoute.tail_position.lat.toFixed(5)}, {viewingRoute.tail_position.lng.toFixed(5)}</p>}
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Lead/Tail Position Dialog */}
      <LeadTailDialog open={showLeadTailDialog} onOpenChange={setShowLeadTailDialog} target={leadTailTarget} onSave={updateLeadTail} />

      {/* Unit Roster — units chosen for this event (event.unit_roster),
          with each unit's current car assignment (derived from `cars`).
          The roster itself is independent of cars; "Edit Roster" opens a
          picker to choose which units are on the event. */}
      <Dialog open={showUnitRoster} onOpenChange={setShowUnitRoster}>
        <DialogContent aria-describedby={undefined} className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Unit Roster</DialogTitle></DialogHeader>
          {(() => {
            const roster = Array.isArray(activeEvent?.unit_roster) ? activeEvent.unit_roster : [];
            const unitToCar = {};
            for (const c of cars) {
              if (c.unit_number) unitToCar[c.unit_number] = c.car_number;
            }
            const sorted = roster
              .slice()
              .sort((a, b) => String(a).localeCompare(String(b)));
            const total = sorted.length;
            const assigned = sorted.filter(name => unitToCar[name]).length;
            const available = total - assigned;
            return (
              <>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <p className="text-xs text-muted-foreground">
                    {total} total · <span className="text-emerald-600">{available} available</span> · {assigned} assigned
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setUnitRosterDraft(roster.slice());
                      setShowUnitRosterEdit(true);
                    }}
                  >
                    Edit Roster
                  </Button>
                </div>
                <div className="border rounded p-2 space-y-1 max-h-[60vh] overflow-y-auto mt-2">
                  {sorted.length === 0 && (
                    <p className="text-sm text-muted-foreground italic text-center py-2">
                      No units on this event yet — click Edit Roster to add some.
                    </p>
                  )}
                  {sorted.map((name) => {
                    const assignedTo = unitToCar[name];
                    return (
                      <div
                        key={name}
                        className="flex items-center justify-between gap-2 px-2 py-1.5 rounded text-sm bg-muted/30"
                      >
                        <span className="font-mono">{name}</span>
                        {assignedTo
                          ? <Badge variant="outline" className="text-[10px]">Car {assignedTo}</Badge>
                          : <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-600/50">Available</Badge>
                        }
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Edit Roster — pick which app units are on this event. */}
      <Dialog open={showUnitRosterEdit} onOpenChange={setShowUnitRosterEdit}>
        <DialogContent aria-describedby={undefined} className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Unit Roster</DialogTitle></DialogHeader>
          {(() => {
            const activeUnits = (appUnits || [])
              .filter(u => u.is_active !== false)
              .slice()
              .sort((a, b) => String(a.name).localeCompare(String(b.name)));
            // Surface stale roster entries (in roster but no longer in appUnits)
            // so they can be unticked.
            const known = new Set(activeUnits.map(u => u.name));
            const stale = unitRosterDraft.filter(n => !known.has(n));
            const draftSet = new Set(unitRosterDraft);
            const toggle = (name) => {
              setUnitRosterDraft(prev =>
                prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
              );
            };
            return (
              <>
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="text-muted-foreground">{unitRosterDraft.length} selected</span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setUnitRosterDraft(activeUnits.map(u => u.name))}
                    >
                      Select all
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setUnitRosterDraft([])}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
                <div className="border rounded p-2 space-y-1 max-h-[55vh] overflow-y-auto mt-2">
                  {activeUnits.length === 0 && stale.length === 0 && (
                    <p className="text-sm text-muted-foreground italic text-center py-2">No active units defined.</p>
                  )}
                  {activeUnits.map((u) => (
                    <label
                      key={u.unit_id || u.name}
                      className="flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-muted/50 cursor-pointer"
                    >
                      <Checkbox
                        checked={draftSet.has(u.name)}
                        onCheckedChange={() => toggle(u.name)}
                      />
                      <span className="font-mono">{u.name}</span>
                    </label>
                  ))}
                  {stale.map((name) => (
                    <label
                      key={`stale-${name}`}
                      className="flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-muted/50 cursor-pointer"
                    >
                      <Checkbox
                        checked={draftSet.has(name)}
                        onCheckedChange={() => toggle(name)}
                      />
                      <span className="font-mono">{name}</span>
                      <span className="text-[10px] text-muted-foreground">(removed)</span>
                    </label>
                  ))}
                </div>
                <div className="flex justify-end gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => setShowUnitRosterEdit(false)}>Cancel</Button>
                  <Button size="sm" onClick={() => saveUnitRoster(unitRosterDraft)}>Save</Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Export Cars Dialog — shows the formatted "Car N: <unit>\n
          location (position)\n..." text for one car or all cars. */}
      <Dialog open={exportCarsText !== null} onOpenChange={(open) => { if (!open) setExportCarsText(null); }}>
        <DialogContent aria-describedby={undefined} className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Export — Cars</DialogTitle></DialogHeader>
          <Textarea
            value={exportCarsText || ""}
            onChange={(e) => setExportCarsText(e.target.value)}
            className="font-mono text-xs"
            rows={18}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(exportCarsText || "");
                  toast.success("Copied to clipboard");
                } catch {
                  toast.error("Copy failed — select and copy manually");
                }
              }}
            >
              Copy
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const blob = new Blob([exportCarsText || ""], { type: "text/plain;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${activeEvent?.name?.replace(/\s+/g, "_") || "event"}_cars.txt`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Download .txt
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Posts Order Dialog (plan mode) — reorder posts to define
          the list shown when Sort: Custom is on. */}
      <CustomPostsOrderDialog
        open={showCustomOrderDialog}
        onOpenChange={setShowCustomOrderDialog}
        posts={posts}
        initialOrder={activeEvent?.posts_custom_order || []}
        onSave={async (newOrder) => {
          try {
            const { data } = await ax.put(
              `${API}/event-ops/events/${activeEvent.event_id}`,
              { posts_custom_order: newOrder }
            );
            setActiveEvent(data);
            toast.success("Custom order saved");
            setShowCustomOrderDialog(false);
          } catch {
            toast.error("Failed to save order");
          }
        }}
      />

      {/* Add Update Dialog */}
      <UpdateDialog
        open={showAddUpdate}
        onOpenChange={setShowAddUpdate}
        posts={posts}
        cars={cars}
        appUnits={appUnits}
        onSave={saveUpdate}
      />

      {/* "Show all posts for this car" dialog — opens from the Cars-subtab
          card "Show all N posts" button. Lists every post in the car's
          sequence with status color, current marker, image, and tap-to-open.
          Long-press (1s) on a row to start drag-reordering. */}
      <Dialog open={allPostsCarNumber != null} onOpenChange={(open) => { if (!open) setAllPostsCarNumber(null); }}>
        <DialogContent aria-describedby={undefined} className="max-w-lg max-h-[85vh] overflow-y-auto">
          {(() => {
            const carObj = cars.find(c => String(c.car_number) === String(allPostsCarNumber));
            if (!carObj) return null;
            const seq = allPostsSeq;
            const seqWithPosts = seq.map((pn) => {
              const p = posts.find((pp) => String(pp.post_number) === String(pn));
              return { pn, post: p, status: p?.status || "pending" };
            });
            const currentIdxFull = carObj.current_post != null
              ? seq.findIndex((s) => String(s) === String(carObj.current_post))
              : -1;
            return (
              <>
                <DialogHeader>
                  <DialogTitle>
                    Car {carObj.car_number} — All Posts
                    {carObj.unit_number ? ` (Unit ${carObj.unit_number})` : ""}
                  </DialogTitle>
                </DialogHeader>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <p className="text-[11px] text-muted-foreground">Tap a post to view details. Hold 1s and drag to reorder.</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      if (!allPostsTextEdit) {
                        setAllPostsText(allPostsSeq.join(", "));
                        setAllPostsTextEdit(true);
                      } else {
                        setAllPostsTextEdit(false);
                      }
                    }}
                  >
                    <Edit className="h-3.5 w-3.5 mr-1" />
                    {allPostsTextEdit ? "Cancel" : "Edit as text"}
                  </Button>
                </div>
                {allPostsTextEdit && (() => {
                  // Live validation against this car's actual sequence.
                  // Only post numbers already assigned to the car are
                  // allowed — text-edit is reorder-only, not add/remove.
                  const haveSet = new Set(allPostsSeq.map(p => String(p)));
                  const parsed = allPostsText
                    .split(",")
                    .map(s => s.trim())
                    .filter(Boolean);
                  const seenSet = new Set();
                  const dupes = [];
                  const unknowns = [];
                  for (const p of parsed) {
                    if (!haveSet.has(p)) unknowns.push(p);
                    if (seenSet.has(p)) dupes.push(p);
                    seenSet.add(p);
                  }
                  const missing = [...haveSet].filter(p => !seenSet.has(p));
                  const isValid =
                    parsed.length === allPostsSeq.length &&
                    unknowns.length === 0 &&
                    dupes.length === 0 &&
                    missing.length === 0;
                  const allowed = [...haveSet].sort((a, b) => {
                    const an = parseInt(a, 10), bn = parseInt(b, 10);
                    return !isNaN(an) && !isNaN(bn) ? an - bn : a.localeCompare(b);
                  });
                  return (
                    <div className="border rounded p-2 bg-muted/30 mt-2 space-y-2">
                      <Label className="text-xs">
                        Reorder by typing post #s (comma-separated). Only posts
                        already in this car's sequence are allowed.
                      </Label>
                      <p className="text-[10px] text-muted-foreground">
                        Allowed: <span className="font-mono">{allowed.join(", ")}</span>
                      </p>
                      <Input
                        value={allPostsText}
                        onChange={(e) => setAllPostsText(e.target.value)}
                        placeholder="e.g. 3, 5, 4"
                        autoFocus
                        className={!isValid && parsed.length > 0 ? "border-red-500 focus-visible:ring-red-500" : ""}
                      />
                      {(unknowns.length > 0 || dupes.length > 0 || (parsed.length > 0 && missing.length > 0)) && (
                        <ul className="text-[11px] text-red-500 space-y-0.5">
                          {unknowns.length > 0 && (
                            <li>Not in this car: <span className="font-mono">{unknowns.join(", ")}</span></li>
                          )}
                          {dupes.length > 0 && (
                            <li>Duplicate: <span className="font-mono">{dupes.join(", ")}</span></li>
                          )}
                          {parsed.length > 0 && missing.length > 0 && (
                            <li>Missing: <span className="font-mono">{missing.join(", ")}</span></li>
                          )}
                        </ul>
                      )}
                      <Button
                        type="button"
                        size="sm"
                        disabled={!isValid}
                        onClick={async () => {
                          // Preserve original value type (int vs string).
                          const next = parsed.map(p => {
                            const orig = allPostsSeq.find(o => String(o) === p);
                            return orig !== undefined ? orig : (p.match(/^\d+$/) ? parseInt(p, 10) : p);
                          });
                          setAllPostsSeq(next);
                          setAllPostsTextEdit(false);
                          try {
                            await ax.put(
                              `${API}/event-ops/events/${activeEvent.event_id}/cars/${encodeURIComponent(allPostsCarNumber)}/reorder`,
                              { post_sequence: next }
                            );
                            fetchEventData(activeEvent.event_id);
                          } catch (e) {
                            toast.error("Failed to save");
                            const carObj2 = cars.find(c => String(c.car_number) === String(allPostsCarNumber));
                            setAllPostsSeq(carObj2?.post_sequence ? [...carObj2.post_sequence] : []);
                          }
                        }}
                      >
                        <Save className="h-3.5 w-3.5 mr-1" /> Save order
                      </Button>
                    </div>
                  );
                })()}
                <div
                  className="space-y-2 mt-2"
                  onPointerMove={handleReorderRowMove}
                  onPointerUp={handleReorderRowUp}
                  onPointerCancel={handleReorderRowUp}
                  style={{ touchAction: dragArmedIdx !== null ? "none" : "auto" }}
                >
                  {seqWithPosts.length === 0 && (
                    <p className="text-sm text-muted-foreground">No posts in sequence.</p>
                  )}
                  {seqWithPosts.map(({ pn, post: pInfo, status }, i) => {
                    if (!pInfo) return (
                      <div key={`${pn}-${i}`} className="p-2 rounded border bg-muted/40 text-xs text-muted-foreground">
                        Post #{pn} (not found)
                      </div>
                    );
                    const isCurrent = i === currentIdxFull;
                    const colorClass = statusColor[status] || statusColor.pending;
                    const isArmed = dragArmedIdx === i;
                    // Drop indicator placement: draw a 2px blue bar above
                    // the hover target when dropping into a position above
                    // the armed row's current spot, below it when dropping
                    // further down. Makes "where will it land" obvious
                    // without doing a full live-reorder.
                    const dropping = dragArmedIdx !== null && dragHoverIdx !== null && dragArmedIdx !== dragHoverIdx;
                    const showDropAbove = dropping && dragHoverIdx === i && dragHoverIdx <= dragArmedIdx;
                    const showDropBelow = dropping && dragHoverIdx === i && dragHoverIdx > dragArmedIdx;
                    return (
                      <React.Fragment key={`${pn}-${i}`}>
                        {showDropAbove && (
                          <div className="h-1 -my-0.5 bg-blue-500 rounded-full shadow-[0_0_8px_rgba(59,130,246,0.7)]" />
                        )}
                      <div
                        ref={(el) => { dragRowRefs.current[i] = el; }}
                        className={`p-2 rounded border cursor-pointer hover:opacity-90 ${colorClass}
                          ${isCurrent ? "ring-2 ring-yellow-400" : ""}
                          ${isArmed ? "ring-2 ring-blue-400 opacity-60 scale-[0.98]" : ""}
                          transition-transform`}
                        onPointerDown={handleReorderRowDown(i)}
                        onClick={() => {
                          // Don't open the post dialog if a drag just happened.
                          if (dragArmedIdx !== null) return;
                          // Leave allPostsCarNumber set so closing the post
                          // dialog returns the user here instead of dropping
                          // them all the way back to the car card.
                          setViewingPost(pInfo);
                        }}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 text-xs font-semibold">
                              <span className="font-mono">#{pInfo.post_number}</span>
                              {isCurrent && <Badge variant="outline" className="text-[9px] bg-white/20 border-white/40 text-white">CURRENT</Badge>}
                              {pInfo.position && <Badge variant="outline" className="text-[9px] bg-white/20 border-white/40 text-white">{pInfo.position}</Badge>}
                            </div>
                            <p className="text-sm font-medium truncate">{pInfo.location}</p>
                            {pInfo.specific && <p className="text-[11px] opacity-80 truncate">{pInfo.specific}</p>}
                            {pInfo.notes && <p className="text-[11px] opacity-80 truncate">{pInfo.notes}</p>}
                          </div>
                          <Badge variant="outline" className="text-[10px] bg-white/20 border-white/40 text-white shrink-0">
                            {status}
                          </Badge>
                        </div>
                      </div>
                      {showDropBelow && (
                        <div className="h-1 -my-0.5 bg-blue-500 rounded-full shadow-[0_0_8px_rgba(59,130,246,0.7)]" />
                      )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Shared destructive-action confirm dialog — replaces window.confirm
          across delete handlers. Populate via askConfirm({title, ...}). */}
      <AlertDialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmAction?.title}</AlertDialogTitle>
            {confirmAction?.description && (
              <AlertDialogDescription>{confirmAction.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={confirmAction?.destructive ? "bg-red-600 hover:bg-red-700 text-white" : ""}
              onClick={() => {
                const fn = confirmAction?.onConfirm;
                setConfirmAction(null);
                if (fn) fn();
              }}
            >
              {confirmAction?.confirmLabel || "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Post ring popup — fires when a post's ring_at hits and the
          backend marks ring_sent_at. Shown over the screen until dismissed. */}
      <Dialog open={!!activeRing} onOpenChange={(open) => { if (!open) dismissRing(); }}>
        <DialogContent aria-describedby={undefined} className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-500">
              <Clock className="h-5 w-5 animate-pulse" />
              Post Ring Alert
            </DialogTitle>
          </DialogHeader>
          {activeRing && (
            <div className="space-y-3">
              <div className="text-center py-2">
                <p className="text-3xl font-bold">Post #{activeRing.post_number}</p>
                <p className="text-lg mt-1">{activeRing.location}</p>
                {activeRing.specific && <p className="text-sm text-muted-foreground">{activeRing.specific}</p>}
              </div>
              <p className="text-center text-sm text-amber-600 font-medium">
                Time for this post.
              </p>
              <Button className="w-full" onClick={dismissRing}>Acknowledge</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────

// Unit-only view: filters cars to the user's linked_unit and renders each
// car in live format with its post sequence. Tapping any post opens the
// shared post-info dialog (via setViewingPost). When canEdit is on, each
// post offers status-change buttons that hit the same endpoints the
// dispatch posts table uses.
function UnitView({ user, activeEvent, events, cars, posts, routes,
                   viewingPost, setViewingPost, canEdit, canSwitch,
                   onSwitchView, actions }) {
  const linkedUnit = user?.linked_unit;
  const myCars = (cars || []).filter(c =>
    linkedUnit && c.unit_number && String(c.unit_number) === String(linkedUnit)
  );
  const liveEvent = (events || []).find(e => e.status === "live");
  const isLive = activeEvent?.status === "live";

  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  // car_number whose unit-side All Posts dialog is open.
  const [unitAllPostsCar, setUnitAllPostsCar] = useState(null);

  const header = (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Car className="h-5 w-5" /> My Unit{linkedUnit ? ` — ${linkedUnit}` : ""}
        </h1>
        {activeEvent && (
          <Badge variant={isLive ? "destructive" : "outline"}>
            {activeEvent.name}{isLive ? " — LIVE" : ""}
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground font-mono sm:hidden">
          {currentTime.toLocaleString('en-US', { timeZone: 'America/New_York', hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </span>
        <span className="text-base text-muted-foreground font-mono hidden sm:block">
          {currentTime.toLocaleString('en-US', { timeZone: 'America/New_York', hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </span>
        {canSwitch && (
          <Button variant="outline" size="sm" onClick={onSwitchView}>Switch View</Button>
        )}
      </div>
    </div>
  );

  if (!linkedUnit) {
    return (
      <div className="p-4 md:p-6">
        {header}
        <Card><CardContent className="pt-6 text-center text-muted-foreground">
          Your account isn't linked to a unit. Ask an admin to set your linked unit in Settings.
        </CardContent></Card>
      </div>
    );
  }

  if (!liveEvent && !activeEvent) {
    return (
      <div className="p-4 md:p-6">
        {header}
        <Card><CardContent className="pt-6 text-center text-muted-foreground">
          No live event right now. This view will populate when an event goes live.
        </CardContent></Card>
      </div>
    );
  }

  if (myCars.length === 0) {
    return (
      <div className="p-4 md:p-6">
        {header}
        <Card><CardContent className="pt-6 text-center text-muted-foreground">
          No cars are assigned to unit {linkedUnit} for this event.
        </CardContent></Card>
      </div>
    );
  }


  return (
    <div className="p-4 md:p-6">
      {header}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {myCars.map((car) => {
          const seq = car.post_sequence || [];
          const seqPosts = seq
            .map((pn) => posts.find((p) => String(p.post_number) === String(pn)))
            .filter(Boolean);
          // Cleared posts are skipped from current/next — but still
          // listed below so the unit can see them with their cleared
          // badge.
          const visibleSeqPosts = seqPosts.filter((p) => p.status !== "cleared");
          const currentVisIdx = car.current_post != null
            ? visibleSeqPosts.findIndex((p) => String(p.post_number) === String(car.current_post))
            : -1;
          // When current_post isn't in visibleSeqPosts (no current set, or
          // it points at a cleared post), treat the first uncleared post as
          // current and surface the second as next — otherwise nextPost
          // silently disappeared.
          const effectiveCurIdx = currentVisIdx >= 0 ? currentVisIdx : (visibleSeqPosts.length > 0 ? 0 : -1);
          const currentPost = effectiveCurIdx >= 0 ? visibleSeqPosts[effectiveCurIdx] : null;
          const nextPost = effectiveCurIdx >= 0 && effectiveCurIdx < visibleSeqPosts.length - 1
            ? visibleSeqPosts[effectiveCurIdx + 1] : null;
          const currentIdx = car.current_post != null
            ? seqPosts.findIndex((p) => String(p.post_number) === String(car.current_post))
            : -1;

          // Live derived car-status (mirrors the dispatch Cars-subtab logic).
          let derivedStatus = car.status || "standby";
          let derivedLabel = derivedStatus;
          if (seqPosts.length > 0) {
            const has = (s) => seqPosts.some((p) => p.status === s);
            const all = (s) => seqPosts.every((p) => p.status === s);
            if (has("enroute")) { derivedStatus = "en_route"; derivedLabel = "en route"; }
            else if (all("cleared")) { derivedStatus = "done"; derivedLabel = "all cleared"; }
            else if (has("covered")) { derivedStatus = "posted"; derivedLabel = "posted"; }
            else { derivedStatus = "standby"; derivedLabel = "standby"; }
          }
          const derivedBadgeClass =
            derivedStatus === "en_route" ? statusColor.enroute :
            carStatusColor[derivedStatus] || carStatusColor.standby;

          return (
            <Card key={car.car_number} className="border-2">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Car {car.car_number}</CardTitle>
                    {car.role && <p className="text-xs text-muted-foreground">{car.role}</p>}
                  </div>
                  <Badge className={derivedBadgeClass}>{derivedLabel}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {seqPosts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No posts in sequence yet.</p>
                ) : visibleSeqPosts.length === 0 ? (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-center">
                    <p className="text-[10px] text-blue-400 uppercase font-semibold mb-1">Done</p>
                    <p className="text-sm font-medium text-blue-200">
                      All {seqPosts.length} post{seqPosts.length === 1 ? "" : "s"} cleared
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Current Post — same look as dispatch cars card. */}
                    {currentPost && (
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 cursor-pointer hover:bg-green-500/15"
                           onClick={() => setViewingPost(currentPost)}>
                        <p className="text-[10px] text-green-500 uppercase font-semibold mb-1">Current Post</p>
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-bold text-base">Post #{currentPost.post_number}</p>
                            <p className="text-sm">{currentPost.location}</p>
                            {currentPost.specific && <p className="text-xs text-muted-foreground">{currentPost.specific}</p>}
                          </div>
                          {currentPost.position && <Badge variant="outline" className="text-xs shrink-0">{currentPost.position}</Badge>}
                        </div>
                        {/* Action buttons — only on the current post. Once
                            cleared, this post drops out and the next
                            upcoming becomes current automatically. */}
                        {canEdit && (
                          <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-green-500/20" onClick={(e) => e.stopPropagation()}>
                            <Button size="sm" variant="outline" className="h-7 text-xs"
                                    onClick={() => actions.enroutePost(currentPost.post_number)}>En Route</Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs"
                                    onClick={() => actions.checkInPost(currentPost.post_number)}>Check In</Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs"
                                    onClick={() => actions.openPost(currentPost.post_number)}>Open</Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs"
                                    onClick={() => actions.clearPost(currentPost.post_number)}>Clear</Button>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Next Post — read-only preview of what's coming. */}
                    {nextPost && (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 cursor-pointer hover:bg-amber-500/15"
                           onClick={() => setViewingPost(nextPost)}>
                        <p className="text-[10px] text-amber-500 uppercase font-semibold mb-1">Next Post</p>
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-bold text-base">Post #{nextPost.post_number}</p>
                            <p className="text-sm">{nextPost.location}</p>
                            {nextPost.specific && <p className="text-xs text-muted-foreground">{nextPost.specific}</p>}
                          </div>
                          {nextPost.position && <Badge variant="outline" className="text-xs shrink-0">{nextPost.position}</Badge>}
                        </div>
                      </div>
                    )}
                  </>
                )}
                {seqPosts.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => setUnitAllPostsCar(car.car_number)}
                  >
                    Show all {seqPosts.length} posts
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Reuse the dispatch post-info dialog. */}
      <Dialog open={!!viewingPost} onOpenChange={(open) => { if (!open) setViewingPost(null); }}>
        <DialogContent aria-describedby={undefined} className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Post #{viewingPost?.post_number}</DialogTitle>
          </DialogHeader>
          {viewingPost && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-muted-foreground block text-xs">Location</span><span className="font-medium">{viewingPost.location || "-"}</span></div>
                <div><span className="text-muted-foreground block text-xs">Position</span><span className="font-medium">{viewingPost.position || "-"}</span></div>
              </div>
              {viewingPost.specific && <div><span className="text-muted-foreground block text-xs">Specific</span><span>{viewingPost.specific}</span></div>}
              {viewingPost.notes && <div><span className="text-muted-foreground block text-xs">Notes</span><span>{viewingPost.notes}</span></div>}
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-muted-foreground block text-xs">Car</span><span className="font-medium">{viewingPost.assigned_car || "-"}</span></div>
                <div><span className="text-muted-foreground block text-xs">Unit</span><span className="font-medium">{viewingPost.assigned_unit || viewingPost.unit || "-"}</span></div>
              </div>
              <div>
                <span className="text-muted-foreground block text-xs">Status</span>
                <Badge className={statusColor[viewingPost.status] || ""}>{viewingPost.status || "pending"}</Badge>
                {viewingPost.covered_at && <span className="text-xs text-muted-foreground ml-2">Covered: {fmtTime(viewingPost.covered_at)}</span>}
                {viewingPost.opened_at && <span className="text-xs text-muted-foreground ml-2">Opened: {fmtTime(viewingPost.opened_at)}</span>}
                {viewingPost.cleared_at && <span className="text-xs text-muted-foreground ml-2">Cleared: {fmtTime(viewingPost.cleared_at)}</span>}
                {viewingPost.covered_by && <div className="text-xs text-muted-foreground mt-1">By: {viewingPost.covered_by}</div>}
              </div>
              {postRouteIds(viewingPost).length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  {postRouteIds(viewingPost).map((rid) => {
                    const r = routes.find(rt => rt.route_id === rid);
                    if (!r) return null;
                    return (
                      <span key={rid} className="inline-flex items-center gap-1">
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: r.color || "#2563EB" }}></span>
                        <span className="text-xs">{r.name}</span>
                      </span>
                    );
                  })}
                </div>
              )}
              {viewingPost.image_url && (
                <img src={getAuthenticatedMediaUrl(`${API.replace('/api', '')}${viewingPost.image_url}/thumb?size=600`)}
                     alt="" className="w-full max-h-48 object-contain rounded cursor-pointer"
                     onClick={() => window.open(getAuthenticatedMediaUrl(`${API.replace('/api', '')}${viewingPost.image_url}`), '_blank')} />
              )}
              <PostMapPreview post={viewingPost} />
              {canEdit && (
                <div className="flex flex-wrap gap-2 pt-2 border-t">
                  <Button size="sm" variant="outline" className="text-amber-500" onClick={() => { actions.enroutePost(viewingPost.post_number); setViewingPost(null); }}>En Route</Button>
                  <Button size="sm" variant="outline" className="text-green-600" onClick={() => { actions.checkInPost(viewingPost.post_number); setViewingPost(null); }}>Check In</Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => { actions.openPost(viewingPost.post_number); setViewingPost(null); }}>Open</Button>
                  <Button size="sm" variant="outline" className="text-blue-600" onClick={() => { actions.clearPost(viewingPost.post_number); setViewingPost(null); }}>Clear</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Unit-side "All Posts" dialog — same shape as the dispatch one,
          read-only (no drag, no text-edit). Tap a row to open the
          post-info dialog; closing it returns here. */}
      <Dialog open={unitAllPostsCar != null} onOpenChange={(open) => { if (!open) setUnitAllPostsCar(null); }}>
        <DialogContent aria-describedby={undefined} className="max-w-lg max-h-[85vh] overflow-y-auto">
          {(() => {
            const carObj = myCars.find(c => String(c.car_number) === String(unitAllPostsCar));
            if (!carObj) return null;
            const fullSeq = (carObj.post_sequence || []).map((pn) => {
              const p = posts.find((pp) => String(pp.post_number) === String(pn));
              return { pn, post: p, status: p?.status || "pending" };
            });
            const currentIdxFull = carObj.current_post != null
              ? (carObj.post_sequence || []).findIndex((s) => String(s) === String(carObj.current_post))
              : -1;
            return (
              <>
                <DialogHeader>
                  <DialogTitle>
                    Car {carObj.car_number} — All Posts
                    {carObj.unit_number ? ` (Unit ${carObj.unit_number})` : ""}
                  </DialogTitle>
                </DialogHeader>
                <p className="text-[11px] text-muted-foreground mt-1">Tap a post to view details.</p>
                <div className="space-y-2 mt-2">
                  {fullSeq.length === 0 && (
                    <p className="text-sm text-muted-foreground">No posts in sequence.</p>
                  )}
                  {fullSeq.map(({ pn, post: pInfo, status }, i) => {
                    if (!pInfo) return (
                      <div key={`${pn}-${i}`} className="p-2 rounded border bg-muted/40 text-xs text-muted-foreground">
                        Post #{pn} (not found)
                      </div>
                    );
                    const isCurrent = i === currentIdxFull;
                    const colorClass = statusColor[status] || statusColor.pending;
                    return (
                      <div
                        key={`${pn}-${i}`}
                        className={`p-2 rounded border cursor-pointer hover:opacity-90 ${colorClass} ${isCurrent ? "ring-2 ring-yellow-400" : ""}`}
                        onClick={() => setViewingPost(pInfo)}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 text-xs font-semibold">
                              <span className="font-mono">#{pInfo.post_number}</span>
                              {isCurrent && <Badge variant="outline" className="text-[9px] bg-white/20 border-white/40 text-white">CURRENT</Badge>}
                              {pInfo.position && <Badge variant="outline" className="text-[9px] bg-white/20 border-white/40 text-white">{pInfo.position}</Badge>}
                            </div>
                            <p className="text-sm font-medium truncate">{pInfo.location}</p>
                            {pInfo.specific && <p className="text-[11px] opacity-80 truncate">{pInfo.specific}</p>}
                            {pInfo.notes && <p className="text-[11px] opacity-80 truncate">{pInfo.notes}</p>}
                          </div>
                          <Badge variant="outline" className="text-[10px] bg-white/20 border-white/40 text-white shrink-0">
                            {status}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className={`text-2xl font-bold ${color || ""}`}>{value}</p>
          </div>
          {Icon && <Icon className={`h-8 w-8 ${color || "text-muted-foreground"}`} />}
        </div>
      </CardContent>
    </Card>
  );
}

function UpdateDialog({ open, onOpenChange, posts, cars, appUnits = [], onSave }) {
  const [text, setText] = useState("");
  const [targetType, setTargetType] = useState("general");
  const [targetId, setTargetId] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  // Reset on open/close so a stale draft doesn't bleed across uses.
  useEffect(() => {
    if (open) {
      setText("");
      setTargetType("general");
      setTargetId("");
      setPickerSearch("");
    }
  }, [open]);

  const targetOptions = (() => {
    if (targetType === "post") {
      return (posts || []).map(p => ({
        value: String(p.post_number),
        label: `#${p.post_number} — ${p.location || "—"}`,
      }));
    }
    if (targetType === "car") {
      return (cars || []).map(c => ({
        value: String(c.car_number),
        label: `Car ${c.car_number}${c.unit_number ? ` (${c.unit_number})` : ""}`,
      }));
    }
    if (targetType === "unit") {
      return (appUnits || [])
        .filter(u => u.is_active !== false)
        .map(u => ({ value: u.name, label: u.name }));
    }
    return [];
  })();
  const filteredOptions = (() => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return targetOptions;
    return targetOptions.filter(o => o.label.toLowerCase().includes(q));
  })();
  const selectedLabel = targetOptions.find(o => o.value === targetId)?.label;

  const submit = () => {
    if (!text.trim()) { toast.error("Update text is required"); return; }
    if (targetType !== "general" && !targetId) {
      toast.error(`Select a ${targetType} to attach to`);
      return;
    }
    onSave({ text: text.trim(), target_type: targetType, target_id: targetType === "general" ? "" : targetId });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-md">
        <DialogHeader><DialogTitle>Add Update</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Update text</Label>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              autoFocus
              placeholder="Type the update…"
            />
          </div>
          <div>
            <Label>Attach to</Label>
            <div className="grid grid-cols-4 gap-1 mt-1">
              {[
                { value: "general", label: "General" },
                { value: "post", label: "Post" },
                { value: "car", label: "Car" },
                { value: "unit", label: "Unit" },
              ].map((t) => (
                <Button
                  key={t.value}
                  type="button"
                  size="sm"
                  variant={targetType === t.value ? "default" : "outline"}
                  className="h-8"
                  onClick={() => { setTargetType(t.value); setTargetId(""); setPickerSearch(""); }}
                >
                  {t.label}
                </Button>
              ))}
            </div>
          </div>
          {targetType !== "general" && (
            <div>
              <Label>Pick {targetType}</Label>
              <Popover open={pickerOpen} onOpenChange={(v) => { setPickerOpen(v); if (!v) setPickerSearch(""); }}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" size="sm" className="w-full justify-start mt-1 font-normal">
                    {selectedLabel || `Select a ${targetType}…`}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[320px] max-w-[90vw] z-[250]" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput value={pickerSearch} onValueChange={setPickerSearch} placeholder="Search…" className="h-9" />
                    <CommandList className="max-h-60 overflow-y-auto">
                      {filteredOptions.length === 0 ? (
                        <CommandEmpty>No match.</CommandEmpty>
                      ) : (
                        <CommandGroup>
                          {filteredOptions.map((o) => (
                            <CommandItem
                              key={o.value}
                              value={o.value}
                              onSelect={() => { setTargetId(o.value); setPickerOpen(false); setPickerSearch(""); }}
                              className={`cursor-pointer ${o.value === targetId ? "font-bold" : ""}`}
                            >
                              {o.label}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}
          <Button onClick={submit}><Save className="h-4 w-4 mr-1" /> Add Update</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Reorder dialog for the per-event custom Posts sort. Long-press a row
// for 1s to arm, drag to reposition, release to commit locally; Save
// persists the new order on the event.
function CustomPostsOrderDialog({ open, onOpenChange, posts, initialOrder, onSave }) {
  const [order, setOrder] = useState([]);

  // Build the working order ONCE per open transition. Re-running on
  // every `posts` change (every 5s during a live event) would wipe the
  // user's in-flight edits. We snapshot `posts` and `initialOrder`
  // refs at open time via ref so the effect can ignore later changes.
  const seedRef = useRef({ posts, initialOrder });
  seedRef.current = { posts, initialOrder };
  useEffect(() => {
    if (!open) return;
    const { posts: seedPosts, initialOrder: seedInitial } = seedRef.current;
    const known = new Set((seedPosts || []).map(p => String(p.post_number)));
    const initial = (seedInitial || []).map(String).filter(s => known.has(s));
    const inOrder = new Set(initial);
    const extras = (seedPosts || [])
      .map(p => String(p.post_number))
      .filter(s => !inOrder.has(s))
      .sort((a, b) => {
        const an = parseInt(a, 10), bn = parseInt(b, 10);
        return !isNaN(an) && !isNaN(bn) ? an - bn : a.localeCompare(b);
      });
    setOrder([...initial, ...extras]);
  }, [open]);

  const move = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j], next[i]];
    setOrder(next);
  };
  // Splice the row at index `i` to 1-based position `targetPos`. Skips
  // no-ops and clamps out-of-range values.
  const moveTo = (i, targetPos) => {
    const n = order.length;
    let j = parseInt(targetPos, 10);
    if (isNaN(j)) return;
    j = Math.max(1, Math.min(n, j)) - 1;
    if (j === i) return;
    const next = [...order];
    const [moved] = next.splice(i, 1);
    next.splice(j, 0, moved);
    setOrder(next);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Custom Posts Order</DialogTitle></DialogHeader>
        <p className="text-[11px] text-muted-foreground mt-1">
          Type a position to jump there, or use the arrows. This is the
          order shown on the Posts list when "Sort: Custom" is on.
        </p>
        <div className="space-y-1 mt-2">
          {order.length === 0 && (
            <p className="text-sm text-muted-foreground italic text-center py-4">No posts yet.</p>
          )}
          {order.map((pn, i) => {
            const p = (posts || []).find(pp => String(pp.post_number) === pn);
            if (!p) return null;
            return (
              <div
                key={pn}
                className="flex items-center gap-2 p-2 rounded border bg-muted/30"
              >
                <Input
                  type="number"
                  min={1}
                  max={order.length}
                  defaultValue={i + 1}
                  key={`pos-${pn}-${i}`}
                  onBlur={(e) => {
                    const v = e.target.value;
                    if (v === "" || parseInt(v, 10) === i + 1) return;
                    moveTo(i, v);
                  }}
                  onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                  className="h-7 w-14 text-xs shrink-0"
                  title="Type a position to move this row there"
                />
                <span className="font-mono w-10 shrink-0">#{p.post_number}</span>
                <span className="text-sm truncate flex-1">{p.location}</span>
                {p.specific && <span className="text-[11px] text-muted-foreground truncate">{p.specific}</span>}
                <div className="flex gap-0.5 shrink-0">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                    title="Move up"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0"
                    disabled={i === order.length - 1}
                    onClick={() => move(i, 1)}
                    title="Move down"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        <Button onClick={() => onSave(order)} className="mt-3">
          <Save className="h-4 w-4 mr-1" /> Save Order
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function PostDialog({ open, onOpenChange, post, cars, routes = [], onSave }) {
  const [form, setForm] = useState({});
  const [geocoding, setGeocoding] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { if (post) setForm({ ...post }); }, [post]);
  if (!post) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload`, formData, { withCredentials: true });
      set("image_url", res.data.url || `/api/uploads/${res.data.filename}`);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const geocodeLocation = async () => {
    const query = form.location;
    if (!query) { toast.error("Enter a location first"); return; }
    setGeocoding(true);
    try {
      const res = await axios.post(`${API}/geocoding/geocode-lookup`, { address: query }, { withCredentials: true });
      const { lat, lng, formatted_address } = res.data;
      setForm((f) => ({ ...f, lat, lng }));
      toast.success(`Found: ${formatted_address || `${lat.toFixed(5)}, ${lng.toFixed(5)}`}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Location not found. Try being more specific.");
    } finally {
      setGeocoding(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{form.post_number ? "Edit Post" : "Add Post"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Post #</Label>
              <Input
                value={form.post_number || ""}
                onChange={(e) => set("post_number", e.target.value)}
                disabled={!!post.post_number}
                title={post.post_number ? "Post number can't be changed after creation" : ""}
              />
              {!!post.post_number && (
                <p className="text-[10px] text-muted-foreground mt-1">Post # can't be changed after creation.</p>
              )}
            </div>
            <div><Label>Position</Label><Input value={form.position || ""} onChange={(e) => set("position", e.target.value)} /></div>
          </div>
          <div><Label>Location</Label><Input value={form.location || ""} onChange={(e) => set("location", e.target.value)} /></div>
          <div><Label>Specific</Label><Input value={form.specific || ""} onChange={(e) => set("specific", e.target.value)} /></div>
          <div><Label>Notes</Label><Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Priority</Label>
              <Select value={form.priority || "none"} onValueChange={(v) => set("priority", v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="No priority" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No priority</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.ring_enabled}
                  onChange={(e) => set("ring_enabled", e.target.checked)} className="w-4 h-4" />
                <span>Ringer reminder</span>
              </Label>
              <Input
                type="datetime-local"
                value={form.ring_at ? (() => {
                  // datetime-local expects LOCAL "YYYY-MM-DDTHH:MM" without
                  // a timezone. toISOString gives UTC, which would render
                  // ~4 hours off from what the user typed.
                  const d = new Date(form.ring_at);
                  const pad = (n) => String(n).padStart(2, "0");
                  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                })() : ""}
                onChange={(e) => set("ring_at", e.target.value ? new Date(e.target.value).toISOString() : "")}
                disabled={!form.ring_enabled}
                className="mt-1"
              />
              {form.ring_enabled && form.ring_sent_at && (
                <p className="text-[10px] text-amber-600 mt-1">Already fired at {new Date(form.ring_sent_at).toLocaleString()}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Assigned Car</Label>
              <Select value={form.assigned_car ? String(form.assigned_car) : "none"} onValueChange={(v) => {
                if (v === "none") {
                  // Clearing the car must also clear the unit — the unit
                  // is derived from the assigned car, never edited directly.
                  setForm((f) => ({ ...f, assigned_car: "", assigned_unit: "" }));
                  return;
                }
                const matchedCar = cars.find((c) => String(c.car_number) === v);
                setForm((f) => ({ ...f, assigned_car: v, assigned_unit: matchedCar?.unit_number || "" }));
              }}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {cars.map((c) => (
                    <SelectItem key={c.car_number} value={String(c.car_number)}>{c.car_number}{c.unit_number ? ` (${c.unit_number})` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Unit</Label>
              <Input
                value={form.assigned_unit || ""}
                readOnly
                disabled
                placeholder="From assigned car"
                title="Unit comes from the assigned car. Change it on the Cars tab."
              />
              <p className="text-[10px] text-muted-foreground mt-1">Set on Cars tab</p>
            </div>
          </div>
          <div>
            <Label>Routes</Label>
            {routes.length === 0 ? (
              <p className="text-xs text-muted-foreground italic mt-1">No routes defined for this event yet.</p>
            ) : (() => {
              // Always work off route_ids (array). On first edit, normalize
              // a legacy single route_id into the array. Saving sends both
              // (route_ids canonical, route_id null) so older code paths
              // don't surface stale data.
              const selected = new Set(postRouteIds(form));
              const toggle = (rid) => {
                const next = new Set(selected);
                if (next.has(rid)) next.delete(rid); else next.add(rid);
                setForm((f) => ({ ...f, route_ids: Array.from(next), route_id: "" }));
              };
              return (
                <div className="border rounded p-2 mt-1 space-y-1 max-h-40 overflow-y-auto">
                  {routes.map((r) => (
                    <label key={r.route_id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5">
                      <input
                        type="checkbox"
                        checked={selected.has(r.route_id)}
                        onChange={() => toggle(r.route_id)}
                        className="w-4 h-4"
                      />
                      <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: r.color || "#2563EB" }} />
                      <span>{r.name}</span>
                    </label>
                  ))}
                </div>
              );
            })()}
          </div>
          {/* Map pin placement */}
          <div className="border rounded-lg p-3 space-y-2 bg-muted/30">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Map Pin</Label>
              <div className="flex gap-1">
                <Button type="button" size="sm" variant="outline" onClick={geocodeLocation} disabled={geocoding || !form.location}>
                  {geocoding ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <MapPin className="h-4 w-4 mr-1" />}
                  {geocoding ? "Finding..." : "Auto"}
                </Button>
                {form.lat && form.lng && (
                  <Button type="button" size="sm" variant="ghost" className="text-red-500 text-xs" onClick={() => setForm((f) => ({ ...f, lat: "", lng: "" }))}>
                    Clear
                  </Button>
                )}
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">Click "Auto" to geocode from location, or click the map to place/move the pin manually.</p>
            <div className="rounded overflow-hidden border" style={{ height: 220 }}>
              <MapContainer
                center={form.lat && form.lng ? [form.lat, form.lng] : CROWN_HEIGHTS_CENTER}
                zoom={form.lat && form.lng ? 17 : 15}
                style={{ height: "100%", width: "100%" }}
                key={`${form.lat || "no"}-${form.lng || "no"}`}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                {form.lat && form.lng && (
                  <Marker
                    position={[form.lat, form.lng]}
                    icon={createPostIcon(form.status || "pending", form.post_number || "?")}
                    draggable={true}
                    eventHandlers={{
                      dragend: (e) => {
                        const { lat, lng } = e.target.getLatLng();
                        setForm((f) => ({ ...f, lat: parseFloat(lat.toFixed(6)), lng: parseFloat(lng.toFixed(6)) }));
                      }
                    }}
                  />
                )}
                <MapClickHandler onMapClick={(lat, lng) => setForm((f) => ({ ...f, lat, lng }))} />
              </MapContainer>
            </div>
            {form.lat && form.lng && (
              <p className="text-[10px] text-green-600">Pin set at {parseFloat(form.lat).toFixed(5)}, {parseFloat(form.lng).toFixed(5)} — drag to adjust</p>
            )}
          </div>
          {/* Image */}
          <div className="border rounded-lg p-3 space-y-2 bg-muted/30">
            <Label className="text-sm font-medium">Post Image</Label>
            {form.image_url ? (
              <div>
                <img src={getAuthenticatedMediaUrl(`${API.replace('/api', '')}${form.image_url}/thumb?size=400`)} alt="Post" className="w-full max-h-40 object-contain rounded mb-2" />
                <div className="flex gap-2">
                  <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground border rounded px-2 py-1">
                    {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Edit className="h-3 w-3" />}
                    {uploading ? "Uploading..." : "Replace"}
                    <input type="file" accept="image/*" className="hidden" onChange={uploadImage} disabled={uploading} />
                  </label>
                  <Button type="button" size="sm" variant="ghost" className="text-red-500 text-xs h-7 px-2" onClick={() => set("image_url", "")}>
                    <Trash2 className="h-3 w-3 mr-1" /> Remove
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <label className="cursor-pointer inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground border rounded px-3 py-2">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  {uploading ? "Uploading..." : "Upload Image"}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadImage} disabled={uploading} />
                </label>
              </div>
            )}
          </div>
          <Button onClick={() => onSave(form)}><Save className="h-4 w-4 mr-1" /> Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CarDialog({ open, onOpenChange, car, posts, cars = [], appUnits = [], onSave }) {
  const [form, setForm] = useState({});
  const [postPickerOpen, setPostPickerOpen] = useState(false);
  const [postSearch, setPostSearch] = useState("");
  useEffect(() => {
    if (car) setForm({ ...car });
  }, [car]);
  if (!car) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // Available pool = posts not in THIS car's sequence AND not already
  // claimed by another car. A post belongs to one car only, so once it's
  // in someone's sequence the picker shouldn't offer it.
  const inSeq = new Set((form.post_sequence || []).map((p) => String(p)));
  const claimedByOther = new Set();
  for (const c of cars) {
    if (String(c.car_number) === String(form.car_number)) continue;
    for (const pn of (c.post_sequence || [])) {
      claimedByOther.add(String(pn));
    }
  }
  const availablePosts = (posts || [])
    .filter((p) => !inSeq.has(String(p.post_number)) && !claimedByOther.has(String(p.post_number)))
    .sort((a, b) => {
      const an = parseInt(a.post_number, 10);
      const bn = parseInt(b.post_number, 10);
      if (!isNaN(an) && !isNaN(bn)) return an - bn;
      return String(a.post_number).localeCompare(String(b.post_number));
    });
  const filteredAvailable = (() => {
    const q = postSearch.trim().toLowerCase();
    if (!q) return availablePosts;
    return availablePosts.filter((p) => {
      const hay = [p.post_number, p.location, p.specific, p.position, p.notes]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  })();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-lg">
        <DialogHeader><DialogTitle>{form.car_number ? "Edit Car" : "Add Car"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Car #</Label><Input value={form.car_number || ""} onChange={(e) => set("car_number", e.target.value)} /></div>
            <div>
              <Label>Unit</Label>
              <Select value={form.unit_number || "none"} onValueChange={(v) => set("unit_number", v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Select unit..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {appUnits.filter((u) => u.is_active !== false).map((u) => (
                    <SelectItem key={u.unit_id || u.name} value={u.name}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Role</Label><Input value={form.role || ""} onChange={(e) => set("role", e.target.value)} placeholder="e.g. Patrol, Lead, Rear" /></div>
          <div>
            <Label>Post Sequence</Label>
            <p className="text-xs text-muted-foreground mb-2">Use arrows to reorder, or pick from existing posts below. Reordering is also drag-supported on the Cars-card sequence.</p>
            {(form.post_sequence || []).length > 0 ? (
              <div className="space-y-1 border rounded p-2 max-h-48 overflow-y-auto">
                {(form.post_sequence || []).map((pn, idx) => {
                  const postData = posts.find((p) => String(p.post_number) === String(pn));
                  return (
                    <div key={`${pn}-${idx}`} className="flex items-center gap-2 text-sm bg-muted/50 rounded px-2 py-1">
                      <span className="font-mono font-bold w-8">{pn}</span>
                      <span className="flex-1 text-muted-foreground truncate">{postData?.location || ""}</span>
                      <div className="flex gap-0.5">
                        <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0" disabled={idx === 0}
                          onClick={() => {
                            const seq = [...(form.post_sequence || [])];
                            [seq[idx - 1], seq[idx]] = [seq[idx], seq[idx - 1]];
                            set("post_sequence", seq);
                          }}>
                          <ChevronUp className="h-3 w-3" />
                        </Button>
                        <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0" disabled={idx === (form.post_sequence || []).length - 1}
                          onClick={() => {
                            const seq = [...(form.post_sequence || [])];
                            [seq[idx], seq[idx + 1]] = [seq[idx + 1], seq[idx]];
                            set("post_sequence", seq);
                          }}>
                          <ChevronDown className="h-3 w-3" />
                        </Button>
                        <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500"
                          onClick={() => set("post_sequence", (form.post_sequence || []).filter((_, i) => i !== idx))}>
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic border rounded p-3 text-center">No posts assigned to this car yet. Assign this car to posts in the Posts tab.</p>
            )}
            <Popover open={postPickerOpen} onOpenChange={(v) => { setPostPickerOpen(v); if (!v) setPostSearch(""); }}>
              <PopoverTrigger asChild>
                <Button type="button" size="sm" variant="outline" className="mt-2 w-full justify-start">
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add a post… ({availablePosts.length} available)
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[320px] max-w-[90vw] z-[250]" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    value={postSearch}
                    onValueChange={setPostSearch}
                    placeholder="Search by # or location…"
                    className="h-9"
                  />
                  <CommandList className="max-h-72 overflow-y-auto">
                    {filteredAvailable.length === 0 ? (
                      <CommandEmpty>
                        {availablePosts.length === 0 ? "No unassigned posts." : "No match."}
                      </CommandEmpty>
                    ) : (
                      <CommandGroup>
                        {filteredAvailable.map((p) => (
                          <CommandItem
                            key={p.post_number}
                            value={String(p.post_number)}
                            onSelect={() => {
                              set("post_sequence", [...(form.post_sequence || []), p.post_number]);
                              setPostSearch("");
                            }}
                            className="cursor-pointer"
                          >
                            <div className="min-w-0">
                              <div className="text-sm">
                                <span className="font-mono font-bold mr-2">#{p.post_number}</span>
                                <span>{p.location || "—"}</span>
                              </div>
                              {(p.specific || p.position) && (
                                <div className="text-[11px] text-muted-foreground truncate">
                                  {[p.position, p.specific].filter(Boolean).join(" · ")}
                                </div>
                              )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <Button onClick={() => {
            onSave({ ...form });
          }}><Save className="h-4 w-4 mr-1" /> Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SchoolDialog({ open, onOpenChange, school, routes = [], onSave }) {
  const [form, setForm] = useState({});
  useEffect(() => { if (school) setForm({ ...school }); }, [school]);
  if (!school) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-lg">
        <DialogHeader><DialogTitle>{form.school_id ? "Edit School" : "Add School"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></div>
          <div>
            <Label>Route</Label>
            <Select value={form.route_id || "none"} onValueChange={(v) => set("route_id", v === "none" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {routes.map((r) => (
                  <SelectItem key={r.route_id} value={r.route_id}>
                    <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: r.color || "#2563EB" }}></span>{r.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Location</Label><Input value={form.location || ""} onChange={(e) => set("location", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Coordinator</Label><Input value={form.coordinator || ""} onChange={(e) => set("coordinator", e.target.value)} /></div>
            <div><Label>Contact</Label><Input value={form.contact || ""} onChange={(e) => set("contact", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Children Count</Label><Input value={form.children_count || ""} onChange={(e) => set("children_count", e.target.value)} /></div>
            <div><Label>Divisions</Label><Input value={form.divisions || ""} onChange={(e) => set("divisions", e.target.value)} /></div>
          </div>
          <div><Label>Street Closures</Label><Input value={form.street_closures || ""} onChange={(e) => set("street_closures", e.target.value)} /></div>
          <div><Label>Notes</Label><Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>
          <Button onClick={() => onSave(form)}><Save className="h-4 w-4 mr-1" /> Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ClosureDialog({ open, onOpenChange, closure, onSave }) {
  const [form, setForm] = useState({});
  useEffect(() => { if (closure) setForm({ ...closure }); }, [closure]);
  if (!closure) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader><DialogTitle>{form.closure_id ? "Edit Closure" : "Add Closure"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Street</Label><Input value={form.street || ""} onChange={(e) => set("street", e.target.value)} /></div>
          <div><Label>Segment</Label><Input value={form.segment || ""} onChange={(e) => set("segment", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Time From</Label><Input value={form.time_from || ""} onChange={(e) => set("time_from", e.target.value)} placeholder="e.g. 9:00 AM" /></div>
            <div><Label>Time To</Label><Input value={form.time_to || ""} onChange={(e) => set("time_to", e.target.value)} placeholder="e.g. 2:00 PM" /></div>
          </div>
          <div><Label>Notes</Label><Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>
          <Button onClick={() => onSave(form)}><Save className="h-4 w-4 mr-1" /> Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Post Map Tab ───────────────────────────────────────────────────────
function PostMapTab({ posts, cars, isLive, labelMode = "post", hidePins = false, routes = [], visibleRouteIds = new Set(), onToggleRoute, onCheckIn, onOpen, onClear, onEnroute, onSetPending, onReassignCar, onMovePin }) {
  const mappablePosts = posts.filter((p) => p.lat && p.lng);
  const unmappedCount = posts.length - mappablePosts.length;

  // Build car lookup for display
  const carLookup = {};
  cars.forEach((c) => { carLookup[c.car_number] = c; });

  const statusLabel = (post) => {
    const s = post.status || "pending";
    const time = post.covered_at || post.opened_at || post.cleared_at;
    const label = s.charAt(0).toUpperCase() + s.slice(1);
    return time ? `${label} at ${fmtTime(time)}` : label;
  };

  return (
    <div className="space-y-3">
      {/* Legend and info bar */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: statusMarkerColor.pending }}></span>
          <span>Pending</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: statusMarkerColor.enroute }}></span>
          <span>En Route</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: statusMarkerColor.open }}></span>
          <span>Open</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: statusMarkerColor.covered }}></span>
          <span>Covered</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: statusMarkerColor.cleared }}></span>
          <span>Cleared</span>
        </div>
        <span className="text-muted-foreground ml-auto">
          {mappablePosts.length} of {posts.length} posts on map
          {unmappedCount > 0 && (
            <span className="text-yellow-600 ml-1">({unmappedCount} missing coordinates)</span>
          )}
        </span>
        {routes.length > 0 && (
          <div className="flex items-center gap-3 ml-4">
            <span className="text-xs text-muted-foreground">Routes:</span>
            {routes.map((r) => (
              <label key={r.route_id} className="flex items-center gap-1 text-xs cursor-pointer">
                <Checkbox checked={visibleRouteIds.has(r.route_id)} onCheckedChange={() => onToggleRoute?.(r.route_id)} className="h-3 w-3" />
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: r.color || "#2563EB" }}></span>
                <span>{r.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Map */}
      <Card className="overflow-hidden border" style={{ height: "calc(100vh - 280px)", minHeight: "400px" }}>
        <CardContent className="p-0 h-full">
          {!hidePins && mappablePosts.length === 0 && posts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-3">
              <MapPin className="h-12 w-12" />
              <p className="text-lg font-medium">No posts with coordinates</p>
              <p className="text-sm">Edit posts and click "Set Location" to add them to the map.</p>
            </div>
          ) : (
            <MapContainer
              center={CROWN_HEIGHTS_CENTER}
              zoom={15}
              style={{ height: "100%", width: "100%" }}
              className="z-0"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <PostMapBoundsHandler posts={mappablePosts} />

              {!hidePins && mappablePosts.map((post) => {
                const car = carLookup[post.assigned_car] || carLookup[post.assigned_car_number];
                return (
                  <Marker
                    key={post.post_number}
                    position={[post.lat, post.lng]}
                    icon={
                      labelMode === "unit" ? createPostIcon(post.status, (post.assigned_unit || "?").replace(/[^0-9]/g, "") || "?") :
                      labelMode === "car" ? createPostIcon(post.status, (post.assigned_car || "?").replace(/^Car\s*/i, "")) :
                      createPostIcon(post.status, post.post_number)
                    }
                  >
                    <Popup minWidth={220} maxWidth={300}>
                      <div className="text-sm space-y-1" style={{ minWidth: "200px" }}>
                        <div className="font-bold text-base border-b pb-1 mb-1">
                          Post #{post.post_number}
                        </div>
                        <div><span className="font-semibold">Location:</span> {post.location || "-"}</div>
                        {post.position && <div><span className="font-semibold">Position:</span> {post.position}</div>}
                        {post.specific && <div><span className="font-semibold">Specific:</span> {post.specific}</div>}
                        {post.notes && <div><span className="font-semibold">Notes:</span> {post.notes}</div>}
                        <div className="border-t pt-1 mt-1">
                          <span className="font-semibold">Car:</span> {post.assigned_car_number || post.assigned_car || "-"}
                          {car?.unit_number && <span> (Unit {car.unit_number})</span>}
                        </div>
                        {post.unit && <div><span className="font-semibold">Unit:</span> {post.unit}</div>}
                        <div className="border-t pt-1 mt-1">
                          <span className="font-semibold">Status:</span>{" "}
                          <span style={{ color: statusMarkerColor[post.status] || "#6b7280", fontWeight: 600 }}>
                            {statusLabel(post)}
                          </span>
                        </div>
                        {isLive && (
                          <div className="border-t pt-2 mt-2 space-y-2">
                            {/* Car reassignment — same options as the list-view "Car" column.
                                Plain <select> so it doesn't escape the Leaflet popup
                                (shadcn Radix Select uses Portal and lands at body root,
                                which renders awkwardly here). */}
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <span className="font-semibold" style={{ fontSize: "12px" }}>Car:</span>
                              <select
                                value={post.assigned_car || ""}
                                onChange={(e) => {
                                  const newCar = e.target.value;
                                  if (newCar !== (post.assigned_car || "")) onReassignCar?.(post.post_number, newCar);
                                }}
                                style={{
                                  // Color/background/border come from index.css's
                                  // `.leaflet-popup-content select` rules so the
                                  // popup matches the active theme (dark/tactical/light).
                                  fontSize: "12px",
                                  padding: "3px 6px",
                                  borderWidth: "1px",
                                  borderStyle: "solid",
                                  borderRadius: "4px",
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              >
                                <option value="">None</option>
                                {cars.map((c) => (
                                  <option key={c.car_number} value={String(c.car_number)}>
                                    {c.car_number}{c.unit_number ? ` (${c.unit_number})` : ""}
                                  </option>
                                ))}
                              </select>
                            </div>

                            {/* Actions / status — mirrors the list-view dropdown exactly. */}
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <span className="font-semibold" style={{ fontSize: "12px" }}>Status:</span>
                              <select
                                value={post.status || "pending"}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v === post.status) return;
                                  const pn = post.post_number;
                                  if (v === "pending") onSetPending?.(pn);
                                  else if (v === "enroute") onEnroute?.(pn);
                                  else if (v === "covered") onCheckIn?.(pn);
                                  else if (v === "open") onOpen?.(pn);
                                  else if (v === "cleared") onClear?.(pn);
                                }}
                                style={{
                                  // Color/background/border come from index.css's
                                  // `.leaflet-popup-content select` rules so the
                                  // popup matches the active theme (dark/tactical/light).
                                  fontSize: "12px",
                                  padding: "3px 6px",
                                  borderWidth: "1px",
                                  borderStyle: "solid",
                                  borderRadius: "4px",
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              >
                                <option value="pending">Pending</option>
                                <option value="enroute">En Route</option>
                                <option value="covered">Covered</option>
                                <option value="open">Open</option>
                                <option value="cleared">Cleared</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
              {routes.filter(r => visibleRouteIds.has(r.route_id)).map((route) => {
                if (!route.waypoints?.length) return null;
                const positions = route.waypoints.map((w) => [w.lat, w.lng]);
                return (
                  <React.Fragment key={route.route_id}>
                    <Polyline positions={positions} color={route.color || "#2563EB"} weight={4} opacity={0.7} />
                    {/* Direction arrows between waypoints */}
                    {positions.length >= 2 && positions.slice(0, -1).map((pos, i) => {
                      const next = positions[i + 1];
                      const mid = getMidpoint(pos, next);
                      const angle = getArrowAngle(pos, next);
                      return <Marker key={`arrow-${i}`} position={mid} icon={createRouteArrowIcon(angle, route.color)} interactive={false} />;
                    })}
                    {/* Start marker */}
                    {positions.length >= 1 && (
                      <Marker position={positions[0]} icon={createRouteEndpointIcon("S", route.color)} />
                    )}
                    {/* Finish marker */}
                    {positions.length >= 2 && (
                      <Marker position={positions[positions.length - 1]} icon={createRouteEndpointIcon("F", route.color)} />
                    )}
                    {/* Live lead/tail */}
                    {route.lead_position && (
                      <Marker position={[route.lead_position.lat, route.lead_position.lng]}
                        icon={createLeadTailIcon("L", route.color)} />
                    )}
                    {route.tail_position && (
                      <Marker position={[route.tail_position.lat, route.tail_position.lng]}
                        icon={createLeadTailIcon("T", route.color)} />
                    )}
                  </React.Fragment>
                );
              })}
            </MapContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Route Dialog ──────────────────────────────────────────────────────
function RouteDialog({ open, onOpenChange, route, onSave }) {
  const [form, setForm] = useState({});
  useEffect(() => { if (route) setForm({ ...route, waypoints: route.waypoints || [] }); }, [route]);
  if (!route) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addWaypoint = (lat, lng) => {
    setForm((f) => ({
      ...f,
      waypoints: [...(f.waypoints || []), { lat: parseFloat(lat.toFixed(6)), lng: parseFloat(lng.toFixed(6)) }],
    }));
  };

  const removeWaypoint = (idx) => {
    setForm((f) => ({ ...f, waypoints: (f.waypoints || []).filter((_, i) => i !== idx) }));
  };

  const updateWaypoint = (idx, lat, lng) => {
    setForm((f) => {
      const wps = [...(f.waypoints || [])];
      wps[idx] = { lat: parseFloat(lat.toFixed(6)), lng: parseFloat(lng.toFixed(6)) };
      return { ...f, waypoints: wps };
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{form.route_id ? "Edit Route" : "Add Route"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Color</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.color || "#2563EB"} onChange={(e) => set("color", e.target.value)} className="w-10 h-9 rounded border cursor-pointer" />
                <Input value={form.color || "#2563EB"} onChange={(e) => set("color", e.target.value)} className="flex-1" />
              </div>
            </div>
            <div><Label>Begin Time</Label><Input type="time" value={form.begin_time || ""} onChange={(e) => set("begin_time", e.target.value)} /></div>
          </div>

          {/* Waypoints Map */}
          <div className="border rounded-lg p-3 space-y-2 bg-muted/30">
            <Label className="text-sm font-medium">Waypoints ({(form.waypoints || []).length})</Label>
            <p className="text-[11px] text-muted-foreground">Click the map to add waypoints. They will be connected in order. Drag markers to adjust.</p>
            <div className="rounded overflow-hidden border" style={{ height: 260 }}>
              <MapContainer
                center={form.waypoints?.length > 0 ? [form.waypoints[0].lat, form.waypoints[0].lng] : CROWN_HEIGHTS_CENTER}
                zoom={15}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                <MapClickHandler onMapClick={addWaypoint} />
                {(form.waypoints || []).length >= 2 && (
                  <Polyline
                    positions={(form.waypoints || []).map((w) => [w.lat, w.lng])}
                    color={form.color || "#2563EB"}
                    weight={4}
                    opacity={0.7}
                  />
                )}
                {(form.waypoints || []).map((wp, idx) => (
                  <Marker
                    key={idx}
                    position={[wp.lat, wp.lng]}
                    draggable={true}
                    icon={L.divIcon({
                      className: "route-wp-marker",
                      html: `<div style="background:${form.color || '#2563EB'};color:white;font-weight:bold;font-size:10px;width:20px;height:20px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.3);">${idx + 1}</div>`,
                      iconSize: [20, 20],
                      iconAnchor: [10, 10],
                    })}
                    eventHandlers={{
                      dragend: (e) => {
                        const { lat, lng } = e.target.getLatLng();
                        updateWaypoint(idx, lat, lng);
                      }
                    }}
                  />
                ))}
              </MapContainer>
            </div>
            {(form.waypoints || []).length > 0 && (
              <div className="max-h-32 overflow-y-auto space-y-1">
                {(form.waypoints || []).map((wp, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs bg-muted/50 rounded px-2 py-1">
                    <span className="font-mono font-bold w-5">{idx + 1}</span>
                    <span className="flex-1 text-muted-foreground">{wp.lat.toFixed(5)}, {wp.lng.toFixed(5)}</span>
                    <Button type="button" size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-500" onClick={() => removeWaypoint(idx)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Button onClick={() => onSave(form)}><Save className="h-4 w-4 mr-1" /> Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Lead/Tail Position Dialog ─────────────────────────────────────────
function LeadTailDialog({ open, onOpenChange, target, onSave }) {
  const [position, setPosition] = useState(null);
  const existingPos = target?.existingPosition;
  useEffect(() => { setPosition(existingPos || null); }, [target]);
  if (!target) return null;

  const mapCenter = existingPos ? [existingPos.lat, existingPos.lng] : CROWN_HEIGHTS_CENTER;
  const label = target.type === "lead" ? "L" : "T";
  const hasNewPosition = position && existingPos && (position.lat !== existingPos.lat || position.lng !== existingPos.lng);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-md">
        <DialogHeader>
          <DialogTitle>Set {target.type === "lead" ? "Lead" : "Tail"} Position</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Click the map to set the {target.type} position.{existingPos ? " Current position shown." : ""}</p>
          <div className="rounded overflow-hidden border" style={{ height: 300 }}>
            <MapContainer
              center={mapCenter}
              zoom={15}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
              <MapClickHandler onMapClick={(lat, lng) => setPosition({ lat, lng })} />
              {hasNewPosition && existingPos && (
                <Marker
                  position={[existingPos.lat, existingPos.lng]}
                  icon={createLeadTailIcon(label, "#666")}
                  opacity={0.4}
                />
              )}
              {position && (
                <Marker
                  position={[position.lat, position.lng]}
                  icon={createLeadTailIcon(label, target.color)}
                />
              )}
            </MapContainer>
          </div>
          {position && (
            <p className="text-xs text-green-600">
              {hasNewPosition ? "New position" : "Current position"}: {position.lat.toFixed(5)}, {position.lng.toFixed(5)}
            </p>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button disabled={!position} onClick={() => onSave(target.routeId, target.type, position.lat, position.lng)}>
              <Save className="h-4 w-4 mr-1" /> Save Position
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
